import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import Modal from "react-bootstrap/Modal";
import Tab from 'react-bootstrap/Tab';
import Nav from 'react-bootstrap/Nav';
import Form from 'react-bootstrap/Form';
import Select, { components } from 'react-select';
import {
    Eye, Search, ChevronDown, ChevronRight, Database, CircleCheck,
    Trash2, Plus, Check, Layers2, FolderOpen, FolderTree,
    Layers, HardDrive, TextSearch, Pencil, Blocks, Copy, SquarePen,
    Maximize2, Minimize2, X
} from 'lucide-react';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import QualdoDataTable from '../../components/bootstrapTable';
import DynamicDatasourceForm from './DynamicDatasourceForm';
// Datasource field metadata is shared with the datasource edit form.
import datasourceConfig from './datasourceConfig.json';
import RcTooltip from 'rc-tooltip';
import {
    getDatasourceIcon,
    getDatasourceStatus
} from "../../utils/configure_utils";

const EMPTY_ARRAY = [];

const customSelectDatasource = (props) => (
    <components.Control {...props} className="has-icon">
        <Database size={18} color="#A4A7AE" />
        {props.children}
    </components.Control>
);

const customSelectSchema = (props) => (
    <components.Control {...props} className="has-icon">
        <FolderTree size={18} color="#A4A7AE" />
        {props.children}
    </components.Control>
);

const DATASOURCE_TYPE_LABELS = {
    snowflake: "Snowflake",
    snow_flake: "Snowflake",
    mysql: "MySQL",
    postgresql: "PostgreSQL",
    azure_blob: "Azure Blob",
    azure_data_lake_gen2: "Azure Data Lake Gen2",
    s3: "AWS S3",
    aws_s3: "AWS S3",
    google_cloud_storage: "Google Cloud Storage",
    bigquery: "BigQuery",
    redshift: "Redshift",
    azure_synapse: "Azure Synapse",
    azure_sql: "Azure SQL",
    databricks: "Databricks"
};

const normalizeDatasourceType = (apiType) => {
    if (!apiType) return "postgresql";
    const value = String(apiType).trim();
    const mapping = {
        "MySQL": "mysql",
        "PostgreSQL": "postgresql",
        "Postgres": "postgresql",
        "Snowflake": "snowflake",
        "snow_flake": "snowflake",
        "Azure Blob": "azure_blob",
        "Azure Data Lake Gen2": "azure_data_lake_gen2",
        "AWS S3": "s3",
        "Google Cloud Storage": "gcs",
        "google_cloud_storage": "gcs",
        "BigQuery": "big_query",
        "bigquery": "big_query",
        "Redshift": "redshift",
        "Azure Synapse": "azure_synapse_ds",
        "azure_synapse": "azure_synapse_ds",
        "Azure SQL": "azure_sql",
        "Databricks": "azure_databricks_table",
        "databricks": "azure_databricks_table",
        "Azure Cosmos DB": "azure_cosmos_gremlin",
        "Google Cloud Spanner": "spanner_graph"
    };
    return mapping[value] || value.toLowerCase().replace(/\s+/g, "_");
};

const getDatasourceTypeOption = (apiType) => {
    const value = normalizeDatasourceType(apiType);
    return {
        label: DATASOURCE_TYPE_LABELS[value] || apiType || value,
        value
    };
};

const getDatasourceTypeOptions = (selectedOption) => {
    const datasourceTypes = datasourceConfig?.datasourceTypes || {};
    const options = Object.keys(datasourceTypes).map((value) => ({
        label: datasourceTypes[value]?.label || DATASOURCE_TYPE_LABELS[value] || value,
        value
    }));

    if (selectedOption && !options.some((option) => option.value === selectedOption.value)) {
        return [selectedOption, ...options];
    }

    return options;
};

const getDatasourceInitialFormData = (datasource = {}) => {
    const config = datasource.datasource_config || datasource.integration_config || {};
    const selectedIntegrationConfig = datasource.integration_config || {};
    const integration = config.integration_names?.[0] || {};
    const authConfig = datasource.auth_config || {};
    const hostValue = config.host_url || config.url || config.host || config.host_name || config.account || "";
    const databaseValue = config.database_name || config.database || "";
    const usernameValue = authConfig.user_name || authConfig.user || authConfig.username || config.user_name || config.username || "";

    return {
        ...config,
        ...authConfig,
        datasource_name: datasource.datasource_name || config.datasource_name || "",
        host_url: hostValue,
        host: hostValue,
        database_name: databaseValue,
        database: databaseValue,
        user_name: usernameValue,
        username: usernameValue,
        password: authConfig.password || config.password || "",
        warehouse: config.ware_house || config.warehouse || "",
        storage_name: config.storage_name || config.account_name || "",
        fileType: selectedIntegrationConfig.filetype || selectedIntegrationConfig.fileType ||
            config.filetype || config.fileType || integration.filetype || integration.fileType || "",
        blob_secret: authConfig.secret_key || authConfig.blob_secret || "",
        accessKey: authConfig.accessKey || authConfig.access_key || config.accessKey || config.access_key || "",
        secretAccessKey: authConfig.secretAccessKey || authConfig.secret_access_key || config.secretAccessKey || config.secret_access_key || ""
    };
};

const hasDisplayValue = (value) => {
    if (value === null || value === undefined) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof File !== "undefined" && value instanceof File) return true;
    return String(value).trim() !== "";
};

const matchesFieldCondition = (actualValue, expectedValue) => {
    const normalizeValue = (value) => (
        typeof value === "string" ? value.toLowerCase() : value
    );
    const normalizedActualValue = normalizeValue(actualValue);

    return Array.isArray(expectedValue)
        ? expectedValue.some((value) => normalizeValue(value) === normalizedActualValue)
        : normalizeValue(expectedValue) === normalizedActualValue;
};

const shouldShowDatasourceField = (field, formData) => {
    if (!field.showWhen) return true;

    const conditions = Array.isArray(field.showWhen)
        ? field.showWhen
        : Object.keys(field.showWhen).map((key) => ({
            field: key,
            value: field.showWhen[key]
        }));

    return conditions.every((condition) => (
        matchesFieldCondition(formData[String(condition.field).trim()], condition.value)
    ));
};

const isDatasourceCredentialGroup = (group = {}) => (
    group.id === "credentials" ||
    (group.fields || []).some((field) => (
        field.name === "username" ||
        field.name === "password" ||
        field.name === "privateKey" ||
        field.input === "radio" ||
        field.input === "password"
    ))
);

const getDatasourceFieldDisplayValue = (field, value) => {
    if (typeof File !== "undefined" && value instanceof File) return value.name;
    if (Array.isArray(value)) return value.join(", ");
    if (field.input === "select" && Array.isArray(field.options)) {
        const option = field.options.find((item) => item.value === value);
        return option?.label || value;
    }
    if (field.input === "password" || field.name === "password") return "********";
    return value;
};

const getDynamicDatasourceRows = (datasource = {}, includeCredentials = false) => {
    const datasourceType = normalizeDatasourceType(
        datasource.connector_type || datasource.datasource_type || datasource.integration_type
    );
    const config = datasourceConfig.datasourceTypes[datasourceType];
    const formData = getDatasourceInitialFormData(datasource);
    const rows = [];

    if (!config || !Array.isArray(config.fieldGroups)) return rows;

    if (includeCredentials && hasDisplayValue(formData.authMethod)) {
        rows.push({
            key: "authMethod",
            label: "Auth Method",
            value: formData.authMethod
        });
    }

    config.fieldGroups.forEach((group) => {
        if (isDatasourceCredentialGroup(group) !== includeCredentials) return;

        (group.fields || []).forEach((field) => {
            const fieldName = String(field.name || "").trim();
            if (!fieldName || fieldName === "datasource_name" || field.input === "radio") return;
            if (!shouldShowDatasourceField(field, formData)) return;

            const value = formData[fieldName];
            if (!hasDisplayValue(value)) return;

            rows.push({
                key: fieldName,
                label: field.label || fieldName,
                value: getDatasourceFieldDisplayValue(field, value)
            });
        });
    });

    return rows;
};

const isBucketDatasource = (datasourceType) => (
    datasourceType === "azure_blob" || datasourceType === "s3" || datasourceType === "azure_data_lake_gen2"
);

const isManualDatasourceMode = (mode) => (
    ["manual", "manualconfig"].includes(String(mode || "").toLowerCase())
);

const getConfiguredSchemaNames = (datasource = {}) => {
    const config = datasource.datasource_config || datasource.integration_config || {};
    const names = config.schema_names || config.buckets || config.schemas || [];
    const directNames = Array.isArray(names) ? names : [];
    const integrationNames = Array.isArray(config.integration_names) ? config.integration_names : [];
    const integrationSchemaNames = integrationNames
        .map((integration) => integration.schema_name || integration.bucket || integration.schema)
        .filter(Boolean);

    return Array.from(new Set([...directNames, ...integrationSchemaNames]));
};

const getSelectedSchemaMode = (datasource = {}) => {
    const config = datasource.datasource_config || {};
    const schemaMode = config.schemaToBeProcessed || config.auto_config_type || datasource.schemaToBeProcessed || "";
    if (schemaMode) {
        return String(schemaMode).toLowerCase().startsWith("specif")
            ? "specificSchemas"
            : "allSchemas";
    }
    return getConfiguredSchemaNames(datasource).length > 0 ? "specificSchemas" : "allSchemas";
};

const getDeclaredSchemaNames = (datasource = {}) => {
    const config = datasource.datasource_config || datasource.integration_config || {};
    const names = config.schema_names || config.buckets || config.schemas || [];
    return Array.isArray(names) ? names : [];
};

const getIntegrationDatasetNames = (integration = {}) => {
    const tableNames = Array.isArray(integration.table_names)
        ? integration.table_names
        : (Array.isArray(integration.integration_config?.table_names) ? integration.integration_config.table_names : []);
    return tableNames
        .map((table) => typeof table === "string" ? table : table.table || table.dataset_name || table.name)
        .filter(Boolean);
};

const getIntegrationSchemaName = (integration = {}) => (
    integration.schema_name ||
    integration.bucket ||
    integration.schema ||
    integration.integration_config?.schema_name ||
    integration.integration_config?.bucket ||
    integration.integration_config?.schema ||
    ""
);

const isStartingIndexQuery = (table = {}) => (
    String(table.start_index_type || "").toLowerCase().replace(/[\s_-]/g, "") === "customquery" ||
    (!table.start_index_type && Boolean(table.start_with_query))
);

const hasEditValidationErrors = (integrations, selectedSchemas, schemaMode, configMode, datasource, integrationSummaries) => {
    if (schemaMode === "specificSchemas" && selectedSchemas.length === 0) return true;
    if (configMode !== "manual") return false;
    if (integrations.length === 0) return true;
    if (integrations.some((config) => !config.schema)) return true;
    const normalizedNames = integrations.map((config) => (config.integrationName || "").trim().toLowerCase());
    if (normalizedNames.some((name) => !name) || new Set(normalizedNames).size !== normalizedNames.length) return true;
    const hasDuplicateExistingName = integrations.some((config) => (
        (integrationSummaries || []).some((summary) =>
            (summary.integration_name || "").trim().toLowerCase() === (config.integrationName || "").trim().toLowerCase() &&
            (summary.integration_id || summary.id) &&
            String(summary.integration_id || summary.id) !== String(config.id) &&
            (!summary.datasource_name || summary.datasource_name === datasource.datasource_name)
        )
    ));
    if (hasDuplicateExistingName) return true;

    return integrations.some((config) => {
        if (config.datasetScope === "specifiedDatasets" && config.incrementalData.length === 0) return true;
        const hasInvalidDataset = config.incrementalData.some((item) => {
            const datasetName = (item.datasetName || "").trim();
            if (!datasetName) return true;
            if (config.datasetScope !== "specifiedDatasets" && !(item.identifier || "").trim()) {
                return true;
            }
            const duplicateInOtherIntegration = integrations.some((otherConfig) =>
                otherConfig.id !== config.id &&
                otherConfig.schema === config.schema &&
                otherConfig.incrementalData.some((otherItem) =>
                    (otherItem.datasetName || "").trim().toLowerCase() === datasetName.toLowerCase()
                )
            );
            const duplicateInExistingIntegration = (Array.isArray(integrationSummaries) ? integrationSummaries : []).some((summary) =>
                getIntegrationSchemaName(summary) === config.schema &&
                String(summary.integration_id || summary.id || "") !== String(config.id) &&
                (summary.integration_name || "") !== config.integrationName &&
                getIntegrationDatasetNames(summary).some((name) => name.trim().toLowerCase() === datasetName.toLowerCase())
            );
            if (duplicateInOtherIntegration || duplicateInExistingIntegration) return true;
            if (item.startingIndexCheckbox && item.startingIndexType === "incremental data identifier" && !(item.startingIndexValue || "").trim()) return true;
            if (item.startingIndexCheckbox && item.startingIndexType === "custom query" && !(item.startingIndexQuery || "").trim()) return true;
            return false;
        });
        const hasInvalidPartition = config.partitions.some((partition) => (
            !(partition.datasetName || "").trim() ||
            !(partition.partitionName || "").trim() ||
            (partition.partitionBy === "Custom Query"
                ? !(partition.customQuery || "").trim()
                : !(partition.partitionIdentifier || "").trim())
        ));
        const partitionDatasetNames = config.partitions
            .map((partition) => (partition.datasetName || "").trim().toLowerCase())
            .filter(Boolean);
        const hasDuplicatePartitionDataset = new Set(partitionDatasetNames).size !== partitionDatasetNames.length;
        return hasInvalidDataset || hasInvalidPartition || hasDuplicatePartitionDataset;
    });
};

const isCustomQueryPartition = (partition = {}) => (
    String(partition.partition_type || partition.partitionBy || "").toLowerCase().replace(/[\s_-]/g, "") === "customquery"
);

const mapDatasourceIntegrationsToConfigs = (datasource = {}, integrationSummaries = []) => {
    const config = datasource.datasource_config || {};
    const integrationConfig = datasource.integration_config || {};
    const definitions = datasource.integration_config
        ? [{
            integration_id: datasource.integration_id,
            integration_name: datasource.integration_name,
            schema_name: integrationConfig.schema_name,
            bucket: integrationConfig.bucket,
            sub_path: integrationConfig.sub_path,
            table_names: integrationConfig.table_names || [],
            partitions: integrationConfig.partitions || [],
            datasetToBeProcessed: integrationConfig.datasetToBeProcessed
        }]
        : (Array.isArray(config.integration_names) ? config.integration_names : []);
    const summaries = Array.isArray(integrationSummaries) ? integrationSummaries : [];
    const summaryByName = new Map(summaries.map((integration) => [integration.integration_name, integration]));

    return definitions.map((definition, index) => {
        const summary = summaryByName.get(definition.integration_name) || {};
        const tableNames = Array.isArray(definition.table_names) ? definition.table_names : [];
        const partitions = Array.isArray(definition.partitions) ? definition.partitions : [];

        return {
            id: definition.integration_id || summary.integration_id || summary.id || `${definition.integration_name || "integration"}_${index}`,
            isPersisted: true,
            integrationName: definition.integration_name || summary.integration_name || "",
            isNameEditing: false,
            isOpen: true,
            isIncrementalOpen: true,
            isPartitionsOpen: true,
            schema: definition.schema_name || definition.bucket || definition.schema || "",
            sub_path: definition.sub_path || "",
            datasetScope: definition.datasetToBeProcessed === "allDataset" || definition.datasetToBeProcessed === "allDatasets"
                ? "allDatasets"
                : (tableNames.length > 0 ? "specifiedDatasets" : "allDatasets"),
            incrementalData: tableNames.map((table, tableIndex) => {
                const startsWithQuery = isStartingIndexQuery(table);
                return {
                    id: `${definition.integration_name || "integration"}_inc_${tableIndex}`,
                    datasetName: table.table || table.dataset_name || "",
                    originalDatasetName: table.table || table.dataset_name || "",
                    isNew: false,
                    exactMatch: table.match_exact === "match_exact" || table.match_exact === true,
                    identifier: table.identifier || table.created_time || table.incremental_identifier || "",
                    startingIndexCheckbox: table.start_index_check_box === true || table.start_index_check_box === "true" || Boolean(table.start_with || table.start_with_query),
                    startingIndexType: startsWithQuery ? "custom query" : "incremental data identifier",
                    startingIndexValue: table.start_with || "",
                    startingIndexQuery: table.start_with_query || "",
                    isPersisted: true
                };
            }),
            partitions: partitions.map((partition, partitionIndex) => {
                const partitionBy = isCustomQueryPartition(partition)
                    ? "Custom Query"
                    : "Column";
                return {
                    id: `${definition.integration_name || "integration"}_part_${partitionIndex}`,
                    datasetName: partition.table || partition.dataset || partition.dataset_name || partition.datasetName || "",
                    partitionName: partition.partition_name || partition.name || "",
                    partitionBy,
                    partitionIdentifier: partition.partition_identifier || partition.column_name || partition.column || "",
                    customQuery: partition.custom_query || partition.partition_query || partition.query || "",
                    isPersisted: true
                };
            })
        };
    });
};

function QueryBox({ query, title = "Starting index query" }) {
    const [copied, setCopied] = useState(false);
    const copy = (e) => {
        e.stopPropagation();
        try {
            if (navigator && navigator.clipboard) {
                navigator.clipboard.writeText(query);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 1400);
        } catch { }
    };

    const truncated = query && query.length > 18 ? query.substring(0, 16) + '...' : query;

    const overlayContent = (
        <div className="tw-max-w-[350px] tw-text-left tw-p-2">
            <div className="tw-text-white tw-text-sm tw-mb-1.5 tw-font-semibold">
                {title}
            </div>
            <div className="tw-text-white/75 tw-text-xs tw-break-words tw-leading-relaxed">
                {query}
            </div>
        </div>
    );

    return (
        <RcTooltip placement="top" overlay={overlayContent} trigger={['hover']}>
            <span
                className="tw-inline-flex tw-items-center tw-gap-1 tw-bg-[#F1F1F1] dark:tw-bg-[#2B2B40] tw-border tw-border-solid tw-border-[#E9EAEB] dark:tw-border-[#363843] tw-rounded tw-px-1.5 tw-py-0.5 tw-cursor-pointer"
            >
                <code className="tw-text-[#484848] dark:tw-text-[#FFFFFF]">{truncated}</code>
                <button
                    type="button"
                    className="tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer tw-text-gray-400 hover:tw-text-gray-700 dark:tw-text-[#A1A5B7] dark:hover:tw-text-white tw-flex tw-items-center"
                    onClick={copy}
                    aria-label="Copy query"
                >
                    {copied ? <Check size={12} className="tw-text-green-500" /> : <Copy size={12} />}
                </button>
            </span>
        </RcTooltip>
    );
}

const DatasourceDetailRow = ({ label, children, isLast = false }) => (
    <div className={`tw-grid tw-grid-cols-[200px_1fr] tw-gap-4 tw-px-5 tw-py-2.5 tw-items-center ${isLast ? "" : "tw-border-0 tw-border-b tw-border-solid tw-border-[#E9EAEB] dark:tw-border-[#2B2B40]"}`}>
        <span className="tw-text-[#475467] dark:tw-text-[#A1A5B7] tw-text-sm">{label}</span>
        <span className="tw-text-[#101828] dark:tw-text-white tw-text-sm tw-font-medium tw-break-all">{children}</span>
    </div>
);

const DatasourceCard = ({ dsInfo, datasource }) => {
    const datasourceType = normalizeDatasourceType(
        datasource?.connector_type || datasource?.datasource_type || datasource?.integration_type || dsInfo.type
    );
    const dynamicRows = getDynamicDatasourceRows(datasource, false);
    const credentialRows = getDynamicDatasourceRows(datasource, true);
    const hasSchemaRow = (dsInfo.specifiedSchemas || []).length > 0;
    const schemaDisplayLabel = isBucketDatasource(datasourceType) ? "Specified buckets" : "Specified schemas";
    const staticTailRows = [
        {
            label: "Configuration mode",
            value: dsInfo.mode || "Manual config",
            show: Boolean(dsInfo.mode)
        },
        {
            label: schemaDisplayLabel,
            value: (
                <div className="tw-flex tw-flex-wrap tw-gap-2">
                    {(dsInfo.specifiedSchemas || []).map((s) => (
                        <span key={s} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1 tw-rounded-full tw-border tw-border-solid tw-border-[#D1E0FF] dark:tw-border-[#2B2B40] tw-bg-[#EFF4FF] dark:tw-bg-[#161622] tw-text-[#3538CD] dark:tw-text-[#53B1FD] tw-text-xs tw-font-medium">
                            <FolderTree size={12} className="tw-text-[#175CD3] dark:tw-text-[#53B1FD]" />
                            {s}
                        </span>
                    ))}
                </div>
            ),
            show: hasSchemaRow
        }
    ].filter((row) => row.show);
    const totalRows = 1 + dynamicRows.length + credentialRows.length + staticTailRows.length;
    let renderedRows = 0;
    const nextIsLast = () => {
        renderedRows += 1;
        return renderedRows === totalRows;
    };

    return (
        <div className="tw-border tw-border-solid tw-border-[#E9EAEB] dark:tw-border-[#2B2B40] tw-rounded-xl tw-bg-white dark:tw-bg-[#1E1E2D] tw-mb-4 tw-overflow-hidden">
            <div className="tw-w-full tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-0 tw-border-b tw-border-solid tw-border-[#E9EAEB] dark:tw-border-[#2B2B40] tw-bg-[#fbfcfe] hover:tw-bg-[#f4f7fb] dark:tw-bg-[#161622] hover:dark:tw-bg-[#252535]">
                <div className="tw-flex tw-items-center tw-gap-2">
                    <Database size={16} className="tw-text-[#475467] dark:tw-text-[#A1A5B7]" />
                    <span className="tw-font-semibold tw-text-base tw-text-[#101828] dark:tw-text-white">
                        {dsInfo.name}
                    </span>
                </div>
                <div>
                    {getDatasourceStatus(dsInfo.status || "Complete")}
                </div>
            </div>

            <DatasourceDetailRow label="Type" isLast={nextIsLast()}>
                <div className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[#101828] dark:tw-text-white tw-text-sm tw-font-medium">
                    {getDatasourceIcon(dsInfo.type || datasourceType)}
                    <span>{datasourceConfig.datasourceTypes[datasourceType]?.label || dsInfo.type || datasourceType}</span>
                </div>
            </DatasourceDetailRow>
            {dynamicRows.map((row) => (
                <DatasourceDetailRow key={row.key} label={row.label} isLast={nextIsLast()}>
                    {row.value}
                </DatasourceDetailRow>
            ))}
            {credentialRows.map((row) => (
                <DatasourceDetailRow key={row.key} label={row.label} isLast={nextIsLast()}>
                    {row.value}
                </DatasourceDetailRow>
            ))}
            {staticTailRows.map((row) => (
                <DatasourceDetailRow key={row.label} label={row.label} isLast={nextIsLast()}>
                    {row.value}
                </DatasourceDetailRow>
            ))}
        </div>
    );
};

const format20Chars = (text, badgeCount) => {
    if (!text || text === "—" || text === "-") return "—";
    const str = String(text);

    const words = str.split(/(?<=[_\s])|(?=\s)/);
    const lines = [];
    let current = "";

    for (const w of words) {
        if ((current + w).length > 20 && current.length > 0) {
            lines.push(current);
            current = w;
        } else {
            current += w;
        }
    }
    if (current) lines.push(current);

    return (
        <span className="tw-inline">
            {lines.map((l, i) => (
                <React.Fragment key={i}>
                    {i > 0 && <br />}
                    {l}
                </React.Fragment>
            ))}
            {badgeCount && badgeCount > 1 ? (
                <span className="tw-inline-flex tw-items-center tw-justify-center tw-ml-1.5 tw-px-1.5 tw-py-0.2 tw-bg-[#f1f4f8] dark:tw-bg-[#2B2B40] tw-text-[#374151] dark:tw-text-[#B2B5BE] tw-rounded-full tw-font-normal tw-align-middle">
                    {badgeCount}
                </span>
            ) : null}
        </span>
    );
};

const getDatasourceViewModel = (datasource = {}, integrationSummaries = []) => {
    const config = datasource.datasource_config || {};
    const configuredIntegrations = Array.isArray(config.integration_names) ? config.integration_names : [];
    const summaries = Array.isArray(integrationSummaries) ? integrationSummaries : [];
    const summaryByName = new Map(
        summaries.map((integration) => [integration.integration_name, integration])
    );
    const datasourceIntegrations = summaries.filter(
        (integration) => integration.datasource_name === datasource.datasource_name
    );

    const definitions = configuredIntegrations.length > 0
        ? configuredIntegrations
        : datasourceIntegrations.map((integration) => ({
            integration_name: integration.integration_name,
            schema_name: integration.schema_name,
            table_names: integration.table_names || [],
            partitions: integration.partitions || []
        }));

    return {
        datasource: {
            type: datasource.connector_type || '',
            name: datasource.datasource_name || '',
            host: config.url || config.host || config.host_url || config.storage_name || '',
            database: config.database || config.database_name || '',
            username: datasource.auth_config?.user || datasource.auth_config?.username || '',
            password: datasource.auth_config?.password ? '••••••••' : '',
            mode: isManualDatasourceMode(datasource.mode) ? 'Manual config' : 'Autoconfig',
            status: datasource.auto_config_status,
            specifiedSchemas: config.schema_names || config.buckets || []
        },
        integrations: definitions.map((definition) => {
            const summary = summaryByName.get(definition.integration_name) ||
                datasourceIntegrations.find((integration) => integration.integration_name === definition.integration_name) || {};
            const partitions = Array.isArray(definition.partitions) ? definition.partitions : [];
            const tableNames = Array.isArray(definition.table_names) ? definition.table_names : [];
            const datasets = tableNames.map((table) => {
                const partition = partitions.find((item) =>
                    (item.table || item.dataset || item.dataset_name || item.datasetName) ===
                    (table.table || table.dataset_name || table.name)
                ) || {};
                const startsWithQuery = isStartingIndexQuery(table);
                const partitionIsQuery = isCustomQueryPartition(partition);
                return {
                    name: table.table || table.dataset_name || table.name || '',
                    exactMatch: table.match_exact === 'match_exact' || table.match_exact === true,
                    incremental: table.identifier || table.created_time || table.incremental_identifier || '—',
                    startIndex: startsWithQuery ? table.start_with_query : (table.start_with || ''),
                    startIndexType: startsWithQuery ? 'query' : 'value',
                    partition: partition.partition_name || partition.name || '—',
                    partitionBy: partitionIsQuery
                        ? (partition.custom_query || partition.partition_query || partition.query || '')
                        : (partition.partition_identifier || partition.column_name || partition.column || '—'),
                    partitionByType: partitionIsQuery ? 'query' : 'column'
                };
            });
            return {
                integration: definition.integration_name || summary.integration_name || '',
                datasetCount: datasets.length,
                status: summary.discovery_status || datasource.auto_config_status || 'completed',
                schema: definition.schema_name || definition.bucket || config.schema || '—',
                sourceIntegration: {
                    ...definition,
                    ...summary,
                    integration_id: summary.integration_id || summary.id || definition.integration_id || definition.id,
                    integration_name: summary.integration_name || definition.integration_name,
                    datasource_name: summary.datasource_name || datasource.datasource_name
                },
                datasets
            };
        })
    };
};

const getIntegrationViewModel = (integration = {}) => {
    const config = integration.integration_config || {};
    const authConfig = integration.auth_config || {};
    const tableNames = Array.isArray(config.table_names) ? config.table_names : [];
    const partitions = Array.isArray(config.partitions) ? config.partitions : [];
    return {
        datasource: {
            type: integration.connector_type || '',
            name: integration.datasource_name || '',
            host: config.host_url || config.url || config.host || config.storage_name || '',
            database: config.database_name || config.database || '',
            username: authConfig.user_name || authConfig.user || authConfig.username || '',
            password: authConfig.password ? '********' : '',
            mode: isManualDatasourceMode(integration.mode) ? 'Manual config' : 'Autoconfig',
            status: integration.discovery_status || integration.auto_config_status || 'completed',
            specifiedSchemas: config.schema_names || config.buckets || (config.schema_name ? [config.schema_name] : [])
        },
        integration: {
            integration: integration.integration_name || '',
            datasetCount: tableNames.length,
            status: integration.discovery_status || 'completed',
            schema: config.schema_name || config.bucket || '-',
            datasets: tableNames.map((table) => {
                const datasetName = table.table || table.dataset_name || table.name || '';
                const partition = partitions.find((item) =>
                    (item.table || item.dataset || item.dataset_name || item.datasetName) === datasetName
                ) || {};
                const startsWithQuery = isStartingIndexQuery(table);
                const partitionIsQuery = isCustomQueryPartition(partition);
                return {
                    name: datasetName,
                    exactMatch: table.match_exact === 'match_exact' || table.match_exact === true,
                    incremental: table.identifier || table.created_time || table.incremental_identifier || '-',
                    startIndex: startsWithQuery ? table.start_with_query : (table.start_with || ''),
                    startIndexType: startsWithQuery ? 'query' : 'value',
                    partition: partition.partition_name || partition.name || '-',
                    partitionBy: partitionIsQuery
                        ? (partition.custom_query || partition.partition_query || partition.query || '-')
                        : (partition.partition_identifier || partition.column_name || partition.column || '-'),
                    partitionByType: partitionIsQuery ? 'query' : 'column'
                };
            })
        }
    };
};

const ViewModeContent = React.memo(({ datasource, integrations, contentType = "datasource", onEditIntegration }) => {
    // const [expandedIntegrationIds, setExpandedIntegrationIds] = useState([]);
    const viewModel = contentType === "integration"
        ? (() => {
            const { datasource: dsInfo, integration } = getIntegrationViewModel(datasource);
            return { datasource: dsInfo, integrations: integration.integration ? [integration] : [] };
        })()
        : getDatasourceViewModel(datasource, integrations);
    const { datasource: dsInfo, integrations: integrationsData } = viewModel;

    const tableData = useMemo(() => {
        const headers = ["Integration", "Status", "Schema", "Dataset", "Incremental", "Start index", "Partition", "Partition by", "Action"];
        const rows = integrationsData.map((item) => [
            {
                value: (
                    <div>
                        {/* <button
                            type="button"
                            className="tw-flex tw-items-center tw-gap-2 tw-bg-transparent tw-border-0 tw-p-0 tw-text-left tw-cursor-pointer"
                            // aria-expanded={expandedIntegrationIds.includes(item.integration)} */}
                        {/* > */}
                            {/* {expandedIntegrationIds.includes(item.integration)
                                ? <ChevronDown size={14} />
                                : <ChevronRight size={14} />} */}
                            {getDatasourceIcon(dsInfo.type)}
                            <span className="tw-font-semibold tw-text-[#101828] dark:tw-text-white">
                                {format20Chars(item.integration, item.datasetCount)}
                            </span>
                        {/* </button> */}
                        {/* {expandedIntegrationIds.includes(item.integration) && (
                            <div className="tw-mt-2 tw-ml-5 tw-space-y-2">
                                {(item.datasets || []).map((dataset, index) => (
                                    <div key={`${dataset.name}_${index}`} className="tw-rounded-md tw-bg-[#F9FAFB] dark:tw-bg-[#252535] tw-p-2 tw-text-xs">
                                        <div className="tw-font-medium">{dataset.name}</div>
                                        <div>Incremental: {dataset.incremental || "—"}</div>
                                        <div>Start index: {dataset.startIndex || "—"}</div>
                                        <div>Partition: {dataset.partition || "—"}</div>
                                        <div>Partition by: {dataset.partitionBy || "—"}</div>
                                    </div>
                                ))}
                            </div>
                        )} */}
                    </div>
                ),
                type: "td"
            },
            {
                value: getDatasourceStatus(item.status || "completed"),
                type: "td"
            },
            {
                value: (
                    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-[2px] tw-rounded-lg tw-border tw-border-solid tw-border-[#C7D7FE] dark:tw-border-[#2B2B40] tw-bg-[#EEF4FF] dark:tw-bg-[#161622] tw-text-xs tw-text-[#3538CD] dark:tw-text-[#53B1FD] tw-font-medium">
                        <FolderTree size={12} className='tw-text-[#175CD3] dark:tw-text-[#53B1FD]' />
                        {item.schema}
                    </span>
                ),
                type: "td"
            },
            {
                value: item.datasets && item.datasets.length > 0 ? (
                    <div className="tw-flex tw-flex-col tw-gap-3">
                        {item.datasets.map((d, idx) => (
                            <div key={idx} className="tw-flex tw-flex-col tw-justify-start tw-min-h-[56px]">
                                <div className="tw-flex tw-items-start tw-gap-1.5">
                                    <Layers size={13} className="tw-text-gray-400 dark:tw-text-[#717680] tw-mt-1 tw-shrink-0" />
                                    <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
                                        <span className="tw-text-[#101828] dark:tw-text-white">
                                            {format20Chars(d.name)}
                                        </span>
                                        {d.exactMatch && (
                                            <span className="tw-px-1.5 tw-py-0.5 tw-text-xs tw-bg-[#F5F5F5] dark:tw-bg-[#252535] tw-text-gray-600 dark:tw-text-[#A1A5B7] tw-rounded">
                                                Exact match
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="tw-flex tw-items-start tw-gap-1.5">
                        <Layers size={13} className="tw-text-gray-400 dark:tw-text-[#717680] tw-mt-1 tw-shrink-0" />
                        <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
                            <span className="tw-text-[#101828] dark:tw-text-white">
                                {format20Chars(item.dataset)}
                            </span>
                            {item.exactMatch && (
                                <span className="tw-text-xs tw-px-1.5 tw-py-0.5 tw-bg-[#F5F5F5] dark:tw-bg-[#252535] tw-text-gray-600 dark:tw-text-[#A1A5B7] tw-rounded">
                                    Exact match
                                </span>
                            )}
                        </div>
                    </div>
                ),
                type: "td"
            },
            {
                value: item.datasets && item.datasets.length > 0 ? (
                    <div className="tw-flex tw-flex-col tw-gap-3">
                        {item.datasets.map((d, idx) => (
                            <div key={idx} className="tw-flex tw-flex-col tw-justify-start tw-min-h-[56px] tw-text-[#101828] dark:tw-text-white">
                                {format20Chars(d.incremental)}
                            </div>
                        ))}
                    </div>
                ) : (
                    <span className="tw-text-[#101828] dark:tw-text-white">{format20Chars(item.incremental)}</span>
                ),
                type: "td"
            },
            {
                value: item.datasets && item.datasets.length > 0 ? (
                    <div className="tw-flex tw-flex-col tw-gap-3">
                        {item.datasets.map((d, idx) => (
                            <div key={idx} className="tw-flex tw-flex-col tw-justify-start tw-min-h-[56px] tw-text-[#101828] dark:tw-text-white">
                                {d.startIndexType === 'query' ? (
                                    <QueryBox query={d.startIndex} title="Starting index query" />
                                ) : (
                                    <span className="tw-text-[#101828] dark:tw-text-white">{format20Chars(d.startIndex)}</span>
                                )}
                            </div>
                        ))}
                    </div>
                ) : item.startIndexType === 'query' ? (
                    <QueryBox query={item.startIndex} title="Starting index query" />
                ) : (
                    <span className="tw-text-[#101828] dark:tw-text-white">{format20Chars(item.startIndex)}</span>
                ),
                type: "td"
            },
            {
                value: item.datasets && item.datasets.length > 0 ? (
                    <div className="tw-flex tw-flex-col tw-gap-3">
                        {item.datasets.map((d, idx) => (
                            <div key={idx} className="tw-flex tw-flex-col tw-justify-start tw-min-h-[56px] tw-text-[#101828] dark:tw-text-white">
                                {format20Chars(d.partition)}
                            </div>
                        ))}
                    </div>
                ) : (
                    <span className="tw-text-[#101828] dark:tw-text-white">{format20Chars(item.partition)}</span>
                ),
                type: "td"
            },
            {
                value: item.datasets && item.datasets.length > 0 ? (
                    <div className="tw-flex tw-flex-col tw-gap-3">
                        {item.datasets.map((d, idx) => (
                            <div key={idx} className="tw-flex tw-flex-col tw-justify-start tw-min-h-[56px] tw-text-[#101828] dark:tw-text-white">
                                {d.partitionByType === 'query' ? (
                                    <QueryBox query={d.partitionBy} title="Partition by query" />
                                ) : (
                                    <span className="tw-text-[#101828] dark:tw-text-white">{format20Chars(d.partitionBy)}</span>
                                )}
                            </div>
                        ))}
                    </div>
                ) : item.partitionByType === 'query' ? (
                    <QueryBox query={item.partitionBy} title="Partition by query" />
                ) : (
                    <span className="tw-text-[#101828] dark:tw-text-white">{format20Chars(item.partitionBy)}</span>
                ),
                type: "td"
            },
            {
                value: (
                    <div className="tw-flex tw-items-center tw-justify-center">
                        <button
                            type="button"
                            className="tw-bg-transparent tw-border-0 tw-text-gray-400 hover:tw-text-gray-600 dark:tw-text-[#717680] dark:hover:tw-text-white tw-p-1 tw-cursor-pointer tw-transition-colors"
                            title="Edit integration"
                            aria-label={`Edit integration ${item.integration}`}
                            onClick={() => onEditIntegration?.(item.sourceIntegration)}
                        >
                            <SquarePen size={15} />
                        </button>
                    </div>
                ),
                type: "td"
            }
        ]);
        return { headers, data: rows };
    }, [integrationsData, dsInfo.type, onEditIntegration]);

    return (
        <div className="qd-scroll tw-space-y-4">
            {/* Connection Info Card */}
            <DatasourceCard dsInfo={dsInfo} datasource={datasource} />

            {/* Integrations Header Title */}
            <div className="tw-flex tw-items-center tw-gap-2.5 tw-pt-3 tw-pb-1">
                <h3 className="tw-text-lg tw-font-semibold tw-text-[#484848] dark:tw-text-white tw-m-0">
                    {contentType === "integration" ? "Integration" : "Integrations"}
                </h3>
                <span className="tw-px-3 tw-py-0.5 tw-bg-[#EFF8FF] dark:tw-bg-[#161622] tw-border tw-border-solid tw-border-[#D1E0FF] dark:tw-border-[#2B2B40] tw-text-[#175CD3] dark:tw-text-[#53B1FD] tw-rounded-full tw-font-medium tw-text-sm">
                    {integrationsData.length}
                </span>
            </div>

            {/* React Bootstrap Table maintained uniformly across Qualdo */}
            <QualdoDataTable
                id="staticViewIntegrationsTable"
                component_name="staticViewIntegrationsTable"
                data={tableData}
                paginationSize={5}
            />
        </div>
    );
});

const EditModeContent = React.memo(({ datasource, integrations: integrationSummaries = EMPTY_ARRAY, existingDatasources = EMPTY_ARRAY, contentType = "datasource", onValidationChange, onDraftChange, onTestConnection, onFetchSchemas }) => {
    const datasourceTypeOption = useMemo(
        () => getDatasourceTypeOption(datasource.connector_type || datasource.datasource_type || datasource.integration_type),
        [datasource]
    );
    const datasourceType = datasourceTypeOption.value;
    const datasourceOptions = useMemo(() => getDatasourceTypeOptions(datasourceTypeOption), [datasourceTypeOption]);
    const datasourceFormData = useMemo(() => getDatasourceInitialFormData(datasource), [datasource]);
    const configuredSchemaNames = useMemo(() => getConfiguredSchemaNames(datasource), [datasource]);
    const declaredSchemaNames = useMemo(() => getDeclaredSchemaNames(datasource), [datasource]);
    const bucketDatasource = isBucketDatasource(datasourceType);
    const isIntegrationContent = contentType === "integration";
    const schemaLabel = bucketDatasource ? "Bucket" : "Schema";
    const originalConfigurationMode = isManualDatasourceMode(datasource.mode) ? "manual" : "autoconfig";

    const [isDatasourceOpen, setIsDatasourceOpen] = useState(true);
    const [isSchemaModeOpen, setIsSchemaModeOpen] = useState(false);
    const [isManualConfigOpen, setIsManualConfigOpen] = useState(false);
    const [selectedSchemaMode, setSelectedSchemaMode] = useState(getSelectedSchemaMode(datasource));
    const [configMode, setConfigMode] = useState(isManualDatasourceMode(datasource.mode) ? "manual" : "autoconfig");
    const [showSchemaModal, setShowSchemaModal] = useState(false);
    const [schemaSearchText, setSchemaSearchText] = useState("");
    const [selectedSchemas, setSelectedSchemas] = useState(configuredSchemaNames);
    const [confirmedSchemas, setConfirmedSchemas] = useState(configuredSchemaNames);
    const [fetchedSchemaNames, setFetchedSchemaNames] = useState(null);
    const [isFetchingSchemas, setIsFetchingSchemas] = useState(false);
    const [schemaFetchError, setSchemaFetchError] = useState("");
    const [datasourceFormValues, setDatasourceFormValues] = useState(datasourceFormData);
    const [integrationValidationErrors, setIntegrationValidationErrors] = useState({});
    const [schemaRemovalCandidate, setSchemaRemovalCandidate] = useState(null);
    const [confirmRemoval, setConfirmRemoval] = useState(null);
    const [removalConfirmationText, setRemovalConfirmationText] = useState("");
    const addIntegrationButtonRef = useRef(null);
    const availableSchemaNames = useMemo(() => {
        const values = fetchedSchemaNames !== null
            ? [...fetchedSchemaNames, ...selectedSchemas]
            : configuredSchemaNames;
        return Array.from(new Set(values));
    }, [configuredSchemaNames, fetchedSchemaNames, selectedSchemas]);
    const [isTestingConnection, setIsTestingConnection] = useState(false);
    const handleDatasourceFormDataChange = useCallback((formData) => {
        setDatasourceFormValues(formData);
    }, []);
    const handleTestConnection = useCallback(async (formData) => {
        setIsTestingConnection(true);
        try {
            const response = await onTestConnection(formData, datasourceType);
            if (Array.isArray(response?.schemas)) {
                setFetchedSchemaNames(response.schemas);
            }
            return response;
        } catch (error) {
            return {
                status_code: 500,
                message: error?.message || "Connection failed.",
                stages: [{ name: "Connection", status: false }]
            };
        } finally {
            setIsTestingConnection(false);
        }
    }, [datasourceType, onTestConnection]);
    const handleFetchAvailableSchemas = useCallback(async () => {
        setIsFetchingSchemas(true);
        setSchemaFetchError("");
        try {
            const response = await onFetchSchemas(datasourceFormValues, datasourceType);
            if (response?.status_code !== 200) {
                throw new Error(response?.message || response?.status_message || "Unable to fetch schemas.");
            }
            const schemas = Array.isArray(response.schemas) ? response.schemas : [];
            setFetchedSchemaNames(schemas);
        } catch (error) {
            setSchemaFetchError(error?.message || "Unable to fetch schemas.");
        } finally {
            setIsFetchingSchemas(false);
        }
    }, [datasourceFormValues, datasourceType, onFetchSchemas]);
    const handleOpenSchemaModal = () => {
        setShowSchemaModal(true);
        handleFetchAvailableSchemas();
    };
    const schemaOptions = useMemo(() => {
        return confirmedSchemas.map((schema) => ({ label: schema, value: schema }));
    }, [confirmedSchemas]);
    const filteredSchemaNames = useMemo(() => (
        availableSchemaNames.filter((schema) => schema.toLowerCase().includes(schemaSearchText.toLowerCase()))
    ), [availableSchemaNames, schemaSearchText]);
    const pendingSchemaAdditions = selectedSchemas.filter((schema) => !confirmedSchemas.includes(schema));
    const pendingSchemaRemovals = confirmedSchemas.filter((schema) => !selectedSchemas.includes(schema));
    const hasFetchedAvailableSchemas = Array.isArray(fetchedSchemaNames) && fetchedSchemaNames.length > 0;

    // Seed only from the selected datasource/integration payload; demo fixtures stay out of edit mode.
    const [integrations, setIntegrations] = useState(() => mapDatasourceIntegrationsToConfigs(datasource, integrationSummaries));
    const hasUnnamedIntegration = integrations.some((config) => !(config.integrationName || "").trim());
    const [integrationFocusId, setIntegrationFocusId] = useState(null);
    const [datasetErrorFocus, setDatasetErrorFocus] = useState(null);
    const newIntegrationRef = useRef(null);
    const newIntegrationNameRef = useRef(null);
    const isManualAfterAutoTransition = originalConfigurationMode === "autoconfig" && configMode === "manual";
    const showIntegrationConfigurations = configMode === "manual" || integrations.length > 0;
    const reportIntegrationValidationErrors = useCallback((errors) => {
        setIntegrationValidationErrors(errors);
        const [configId, configErrors] = Object.entries(errors || {}).find(([, configError]) => (
            Object.keys(configError?.datasets || {}).length > 0
        )) || [];
        const itemId = Object.keys(configErrors?.datasets || {})[0];
        if (!configId || !itemId) return;

        setIsDatasourceOpen(false);
        setIsSchemaModeOpen(false);
        setIsManualConfigOpen(true);
        setIntegrations((currentIntegrations) => currentIntegrations.map((config) => {
            const isErroredConfig = String(config.id) === String(configId);
            return {
                ...config,
                isOpen: isErroredConfig,
                isIncrementalOpen: isErroredConfig,
                isPartitionsOpen: false,
                incrementalData: config.incrementalData.map((item) => ({
                    ...item,
                    isOpen: isErroredConfig && String(item.id) === String(itemId)
                }))
            };
        }));
        setDatasetErrorFocus({ inputId: `dataset-input-${configId}-${itemId}` });
    }, []);

    useEffect(() => {
        if (!integrationFocusId || !newIntegrationRef.current) return;
        newIntegrationRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        if (newIntegrationNameRef.current) {
            newIntegrationNameRef.current.focus();
        }
        setIntegrationFocusId(null);
    }, [integrations, integrationFocusId]);

    useEffect(() => {
        if (!datasetErrorFocus) return;
        const input = document.getElementById(datasetErrorFocus.inputId);
        if (!input) return;
        input.focus();
        input.scrollIntoView({ behavior: "smooth", block: "center" });
        setDatasetErrorFocus(null);
    }, [integrations, datasetErrorFocus]);

    useEffect(() => {
        setSelectedSchemaMode(getSelectedSchemaMode(datasource));
        setConfigMode(isManualDatasourceMode(datasource.mode) ? "manual" : "autoconfig");
        setSelectedSchemas(configuredSchemaNames);
        setConfirmedSchemas(configuredSchemaNames);
        setFetchedSchemaNames(null);
        setDatasourceFormValues(datasourceFormData);
        setSchemaSearchText("");
        setShowSchemaModal(false);
        setSchemaRemovalCandidate(null);
        setConfirmRemoval(null);
        setIntegrations(mapDatasourceIntegrationsToConfigs(datasource, integrationSummaries));
    }, [datasource, integrationSummaries, configuredSchemaNames, datasourceFormData]);

    useEffect(() => {
        if (onValidationChange) {
            onValidationChange(hasEditValidationErrors(
                integrations,
                selectedSchemas,
                selectedSchemaMode,
                configMode,
                datasource,
                integrationSummaries
            ));
        }
    }, [integrations, selectedSchemas, selectedSchemaMode, configMode, datasource, integrationSummaries, onValidationChange]);

    useEffect(() => {
        if (onDraftChange) {
            onDraftChange({
                formData: datasourceFormValues,
                datasourceId: datasource.datasource_id || datasource.id,
                originalDatasource: datasource,
                contentType,
                datasourceType,
                schemaModeSelection: selectedSchemaMode,
                configurationMode: configMode,
                selectedSchemas,
                integrationConfigurations: integrations,
                availableSchemas: fetchedSchemaNames || [],
                reportIntegrationValidationErrors
            });
        }
    }, [datasourceFormValues, datasourceType, selectedSchemaMode, configMode, selectedSchemas, integrations, fetchedSchemaNames, onDraftChange, datasource, contentType, reportIntegrationValidationErrors]);

    const handleSchemaModalSelect = (schemaName) => {
        if (selectedSchemas.includes(schemaName) && integrations.some((config) => config.schema === schemaName)) {
            setSchemaRemovalCandidate(schemaName);
            return;
        }
        setSelectedSchemas((currentSchemas) => (
            currentSchemas.includes(schemaName)
                ? currentSchemas.filter((schema) => schema !== schemaName)
                : [...currentSchemas, schemaName]
        ));
    };

    const handleConfirmSchemaRemoval = () => {
        if (!schemaRemovalCandidate) return;
        const schemaNames = Array.isArray(schemaRemovalCandidate) ? schemaRemovalCandidate : [schemaRemovalCandidate];
        setSelectedSchemas((currentSchemas) => currentSchemas.filter((schema) => !schemaNames.includes(schema)));
        setSchemaRemovalCandidate(null);
    };

    const handleSelectAllSchemas = () => {
        if (selectedSchemas.length === availableSchemaNames.length) {
            const schemasWithIntegrations = integrations
                .map((config) => config.schema)
                .filter((schema, index, schemas) => schema && selectedSchemas.includes(schema) && schemas.indexOf(schema) === index);
            if (schemasWithIntegrations.length > 0) {
                setSchemaRemovalCandidate(schemasWithIntegrations);
                return;
            }
        }
        setSelectedSchemas((currentSchemas) => (
            currentSchemas.length === availableSchemaNames.length ? [] : [...availableSchemaNames]
        ));
    };

    const handleConfirmSchemaSelection = () => {
        if (selectedSchemas.length === 0) return;
        setIntegrations((currentIntegrations) => currentIntegrations.map((config) => (
            selectedSchemas.includes(config.schema)
                ? config
                : { ...config, schema: selectedSchemas[0] || "" }
        )));
        setConfirmedSchemas(selectedSchemas);
        setShowSchemaModal(false);
        window.setTimeout(() => addIntegrationButtonRef.current?.focus(), 0);
    };

    const handleSchemaModeChange = (mode) => {
        if (isIntegrationContent || mode === selectedSchemaMode) return;
        setSelectedSchemaMode(mode);
        if (mode === "allSchemas" && configMode === "manual") {
            setConfigMode("autoconfig");
        }
    };

    const handleConfigurationModeChange = (mode) => {
        if (isIntegrationContent || mode === configMode) return;
        if (mode === "manual" && selectedSchemaMode !== "specificSchemas") return;
        if (mode === "manual") {
            setIntegrations((currentIntegrations) => currentIntegrations.map((config) => ({
                ...config,
                datasetScope: "specifiedDatasets"
            })));
        }
        setConfigMode(mode);
    };

    // Integration Actions
    const handleAddIntegration = () => {
        if (hasUnnamedIntegration) return;
        setConfigMode("manual");
        setIsManualConfigOpen(true);
        const integrationId = Date.now().toString();
        setIntegrationFocusId(integrationId);
        setIntegrations([
            ...integrations,
            {
                id: integrationId,
                isPersisted: false,
                integrationName: "",
                isNameEditing: true,
                isOpen: true,
                isIncrementalOpen: true,
                isPartitionsOpen: true,
                schema: schemaOptions[0]?.value || "",
                datasetScope: isManualAfterAutoTransition ? "specifiedDatasets" : "allDatasets",
                incrementalData: [],
                partitions: []
            }
        ]);
    };

    const handleRemoveIntegration = (configId) => {
        const config = integrations.find((item) => item.id === configId);
        if (!config || integrations.length <= 1) return;
        if (!config.isPersisted) {
            setIntegrations((current) => current.filter((item) => item.id !== configId));
            if (!declaredSchemaNames.includes(config.schema)) {
                const otherConfigsUseSchema = integrations.some((item) =>
                    item.id !== config.id && item.schema === config.schema
                );
                if (!otherConfigsUseSchema) {
                    setSelectedSchemas((current) => current.filter((schema) => schema !== config.schema));
                    setConfirmedSchemas((current) => current.filter((schema) => schema !== config.schema));
                }
            }
            return;
        }
        setRemovalConfirmationText("");
        setConfirmRemoval({ type: "integration", configId, label: config.integrationName, isPersisted: config.isPersisted });
    };

    const handleRemoveIncremental = (configId, itemId) => {
        const config = integrations.find((item) => item.id === configId);
        const item = config?.incrementalData.find((incremental) => incremental.id === itemId);
        if (!config || !item) return;
        if (config.datasetScope === "specifiedDatasets" && config.incrementalData.length <= 1) return;

        if (item.isPersisted) {
            setRemovalConfirmationText("");
            setConfirmRemoval({ type: "dataset", configId, itemId, label: item.datasetName, isPersisted: true });
            return;
        }

        setIntegrations((current) => current.map((integration) => (
            integration.id === configId
                ? { ...integration, incrementalData: integration.incrementalData.filter((incremental) => incremental.id !== itemId) }
                : integration
        )));
    };

    const handleConfirmRemoval = () => {
        if (!confirmRemoval) return;
        if (confirmRemoval.isPersisted &&
            removalConfirmationText.trim() !== (confirmRemoval.label || "").trim()) return;
        if (confirmRemoval.type === "integration") {
            if (integrations.length <= 1) return;
            const removedConfig = integrations.find((config) => config.id === confirmRemoval.configId);
            setIntegrations((current) => current.filter((config) => config.id !== confirmRemoval.configId));
            if (removedConfig && !declaredSchemaNames.includes(removedConfig.schema)) {
                const otherConfigsUseSchema = integrations.some((config) =>
                    config.id !== removedConfig.id && config.schema === removedConfig.schema
                );
                if (!otherConfigsUseSchema) {
                    setSelectedSchemas((current) => current.filter((schema) => schema !== removedConfig.schema));
                    setConfirmedSchemas((current) => current.filter((schema) => schema !== removedConfig.schema));
                }
            }
        } else if (confirmRemoval.type === "dataset") {
            const { configId, itemId } = confirmRemoval;
            setIntegrations((current) => current.map((config) => (
                config.id === configId
                    ? { ...config, incrementalData: config.incrementalData.filter((item) => item.id !== itemId) }
                    : config
            )));
        } else if (confirmRemoval.type === "partition") {
            const { configId, itemId } = confirmRemoval;
            setIntegrations((current) => current.map((config) => (
                config.id === configId
                    ? { ...config, partitions: config.partitions.filter((partition) => partition.id !== itemId) }
                    : config
            )));
        }
        setConfirmRemoval(null);
        setRemovalConfirmationText("");
    };

    const handleCancelRemoval = () => {
        setConfirmRemoval(null);
        setRemovalConfirmationText("");
    };

    const handleIntegrationNameChange = (configId, name) => {
        setIntegrations(integrations.map(c => c.id === configId ? { ...c, integrationName: name } : c));
    };

    const handleToggleNameEdit = (configId, isNameEditing) => {
        setIntegrations((current) => current.map((config) => (
            config.id === configId ? { ...config, isNameEditing } : config
        )));
    };

    const handleToggleIntegrationOpen = (configId) => {
        setIntegrations(integrations.map(c => c.id === configId ? { ...c, isOpen: c.isOpen === false } : c));
    };

    const handleToggleIncrementalOpen = (configId) => {
        setIntegrations(integrations.map(c => c.id === configId ? { ...c, isIncrementalOpen: c.isIncrementalOpen === false } : c));
    };

    const handleTogglePartitionsOpen = (configId) => {
        setIntegrations(integrations.map(c => c.id === configId ? { ...c, isPartitionsOpen: c.isPartitionsOpen === false } : c));
    };

    const handleToggleIncItemOpen = (configId, incId) => {
        setIntegrations(integrations.map(config => {
            if (config.id !== configId) return config;
            return {
                ...config,
                incrementalData: config.incrementalData.map(item => item.id === incId ? { ...item, isOpen: item.isOpen === false } : item)
            };
        }));
    };

    const handleTogglePartItemOpen = (configId, partId) => {
        setIntegrations(integrations.map(config => {
            if (config.id !== configId) return config;
            return {
                ...config,
                partitions: config.partitions.map(p => p.id === partId ? { ...p, isOpen: p.isOpen === false } : p)
            };
        }));
    };

    const handleSchemaSelect = (configId, option) => {
        setIntegrations(integrations.map(c => c.id === configId ? { ...c, schema: option ? option.value : "" } : c));
    };

    const handleIntegrationFieldChange = (configId, field, value) => {
        setIntegrations(integrations.map((config) => (
            config.id === configId ? { ...config, [field]: value } : config
        )));
    };

    const handleDatasetScopeChange = (configId, scope) => {
        setIntegrations(integrations.map(c => c.id === configId ? { ...c, datasetScope: scope } : c));
    };

    // Incremental Data Row Actions ("one by one flow")
    const handleAddIncremental = (configId) => {
        setIntegrations(integrations.map(config => {
            if (config.id !== configId) return config;
            return {
                ...config,
                incrementalData: [
                    ...config.incrementalData,
                    {
                        id: Date.now().toString(),
                        datasetName: "",
                        originalDatasetName: "",
                        isNew: true,
                        exactMatch: true,
                        identifier: "",
                        startingIndexCheckbox: false,
                        startingIndexType: "incremental data identifier",
                        startingIndexValue: "",
                        startingIndexQuery: "",
                        isPersisted: false
                    }
                ]
            };
        }));
    };

    const handleIncrementalFieldChange = (configId, incId, field, value) => {
        setIntegrations(integrations.map(config => {
            if (config.id !== configId) return config;
            return {
                ...config,
                incrementalData: config.incrementalData.map(item => item.id === incId
                    ? {
                        ...item,
                        [field]: value,
                        ...(field === "datasetName" && !item.isNew
                            ? { isNew: value.trim() !== (item.originalDatasetName || "").trim() }
                            : {})
                    }
                    : item)
            };
        }));
        if (field === "datasetName") {
            setIntegrationValidationErrors((currentErrors) => {
                const configErrors = currentErrors[configId];
                if (!configErrors?.datasets?.[incId]) return currentErrors;
                const datasets = { ...configErrors.datasets };
                delete datasets[incId];
                const updatedConfigErrors = { ...configErrors, datasets };
                if (Object.keys(datasets).length === 0) delete updatedConfigErrors.datasets;
                const updatedErrors = { ...currentErrors };
                if (Object.keys(updatedConfigErrors).length === 0) delete updatedErrors[configId];
                else updatedErrors[configId] = updatedConfigErrors;
                return updatedErrors;
            });
        }
    };

    // Partition Row Actions ("one by one flow")
    const handleAddPartition = (configId) => {
        setIntegrations(integrations.map(config => {
            if (config.id !== configId) return config;
            return {
                ...config,
                partitions: [
                    ...config.partitions,
                    {
                        id: Date.now().toString(),
                        datasetName: "",
                        partitionName: "",
                        partitionBy: "Column",
                        partitionIdentifier: "",
                        customQuery: "",
                        isPersisted: false
                    }
                ]
            };
        }));
    };

    const handleRemovePartition = (configId, partId) => {
        const config = integrations.find((item) => item.id === configId);
        const partition = config?.partitions.find((item) => item.id === partId);
        if (!partition) return;
        if (partition.isPersisted) {
            setRemovalConfirmationText("");
            setConfirmRemoval({ type: "partition", configId, itemId: partId, label: partition.partitionName, isPersisted: true });
            return;
        }
        setIntegrations(integrations.map(config => {
            if (config.id !== configId) return config;
            return {
                ...config,
                partitions: config.partitions.filter(p => p.id !== partId)
            };
        }));
    };

    const handlePartitionFieldChange = (configId, partId, field, value) => {
        setIntegrations(integrations.map(config => {
            if (config.id !== configId) return config;
            return {
                ...config,
                partitions: config.partitions.map(p => p.id === partId ? { ...p, [field]: value } : p)
            };
        }));
    };

    const hasDuplicateDataset = (configId, schemaName, datasetName) => {
        const normalizedName = (datasetName || "").trim().toLowerCase();
        if (!normalizedName) return false;
        const duplicateInDraft = integrations.some((config) =>
            config.id !== configId &&
            config.schema === schemaName &&
            config.incrementalData.some((item) => (item.datasetName || "").trim().toLowerCase() === normalizedName)
        );
        const currentConfig = integrations.find((config) => config.id === configId);
        const duplicateInExisting = (Array.isArray(integrationSummaries) ? integrationSummaries : []).some((summary) =>
            getIntegrationSchemaName(summary) === schemaName &&
            String(summary.integration_id || summary.id || "") !== String(configId) &&
            (summary.integration_name || "") !== (currentConfig?.integrationName || "") &&
            getIntegrationDatasetNames(summary).some((name) => name.trim().toLowerCase() === normalizedName)
        );
        return duplicateInDraft || duplicateInExisting;
    };

    const hasDuplicateIntegrationName = (configId, name) => {
        const normalizedName = (name || "").trim().toLowerCase();
        if (!normalizedName) return false;
        const duplicateInDraft = integrations.some((config) =>
            config.id !== configId && (config.integrationName || "").trim().toLowerCase() === normalizedName
        );
        if (duplicateInDraft) return true;
        return (Array.isArray(integrationSummaries) ? integrationSummaries : []).some((summary) =>
            (summary.integration_name || "").trim().toLowerCase() === normalizedName &&
            (summary.integration_id || summary.id) &&
            String(summary.integration_id || summary.id) !== String(configId) &&
            (!summary.datasource_name || summary.datasource_name === datasource.datasource_name)
        );
    };

    const getPartitionDatasetOptions = (config, partitionId) => {
        const datasets = config.incrementalData
            .map((item) => (item.datasetName || "").trim())
            .filter(Boolean);
        return Array.from(new Set(datasets)).map((dataset) => ({
            label: dataset,
            value: dataset,
            isDisabled: config.partitions.some((partition) =>
                partition.id !== partitionId && (partition.datasetName || "").trim().toLowerCase() === dataset.toLowerCase()
            )
        }));
    };

    const hasDuplicatePartitionDataset = (config, partitionId, datasetName) => {
        const normalizedName = (datasetName || "").trim().toLowerCase();
        return Boolean(normalizedName) && config.partitions.some((partition) =>
            partition.id !== partitionId && (partition.datasetName || "").trim().toLowerCase() === normalizedName
        );
    };

    return (
        <>
        <div className="qd-scroll tw-space-y-4">
            {/* 1. Datasource Details */}
            {!isIntegrationContent && (
            <div className="tw-space-y-2">
                <button
                    type="button"
                    className="tw-flex tw-items-center tw-gap-1.5 tw-bg-transparent tw-border-0 tw-px-1 tw-py-0 tw-cursor-pointer"
                    onClick={() => {
                        const shouldOpen = !isDatasourceOpen;
                        setIsDatasourceOpen(shouldOpen);
                        if (shouldOpen) setIsSchemaModeOpen(false);
                    }}
                    aria-expanded={isDatasourceOpen}
                >
                    {isDatasourceOpen ? (
                        <ChevronDown size={16} className="tw-text-[#007bff] dark:tw-text-[#007bff]" />
                    ) : (
                        <ChevronRight size={16} className="tw-text-[#007bff] dark:tw-text-[#007bff]" />
                    )}
                    <h6 className="!tw-m-0 tw-text-base tw-font-semibold tw-text-[#007bff] dark:tw-text-[#007bff]">
                        Datasource Details
                    </h6>
                </button>

                {isDatasourceOpen && (
                    <div className="vertical-stepper__content tw-ml-6 tw-mt-2 tw-rounded-xl tw-border tw-border-solid tw-border-[#E9EAEB] dark:tw-border-[#2B2B40] tw-bg-white dark:tw-bg-[#1E1E2D] tw-p-4">
                        <div className="form-row tw-mb-3">
                            <div className="form-group col-lg-6 tw-mb-2">
                                <Form.Label>Choose a Datasource type <span className='text-primary'>*</span></Form.Label>
                                <Select
                                    id="choose_datasource_type_edit_static"
                                    value={datasourceTypeOption}
                                    options={datasourceOptions}
                                    placeholder="Choose Data Source"
                                    classNamePrefix='select-control'
                                    components={{ Control: customSelectDatasource }}
                                    isDisabled={true}
                                />
                            </div>
                        </div>

                        <DynamicDatasourceForm
                            config={datasourceConfig}
                            selectedDatasource={datasourceType}
                            initialFormData={datasourceFormData}
                            isEditMode={true}
                            existingDatasources={existingDatasources}
                            existingIntegrations={integrationSummaries}
                            currentIntegrationName={datasourceFormData.integration_name}
                            currentDatasourceId={datasource.datasource_id || datasource.id}
                            isTestingConnection={isTestingConnection}
                            onFormDataChange={handleDatasourceFormDataChange}
                            onTestConnection={handleTestConnection}
                            onContinueSchemaSelection={() => {
                                setIsDatasourceOpen(false);
                                setIsSchemaModeOpen(true);
                            }}
                        />
                    </div>
                )}
            </div>
            )}

            {/* 2. Schema & Mode Selection */}
            <div className="tw-space-y-2">
                <button
                    type="button"
                    className="tw-flex tw-items-center tw-gap-1.5 tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer tw-px-1"
                    onClick={() => {
                        const shouldOpen = !isSchemaModeOpen;
                        setIsSchemaModeOpen(shouldOpen);
                    }}
                >
                    {isSchemaModeOpen ? (
                        <ChevronDown size={16} className="tw-text-[#007bff] dark:tw-text-[#007bff]" />
                    ) : (
                        <ChevronRight size={16} className="tw-text-[#007bff] dark:tw-text-[#007bff]" />
                    )}
                    <h6 className="!tw-m-0 tw-text-base tw-font-semibold tw-text-[#007bff] dark:tw-text-[#007bff]">
                        {bucketDatasource ? "Bucket & Mode Selection" : "Schema & Mode Selection"}
                    </h6>
                </button>

                {isSchemaModeOpen && (
                    <div className="tw-ml-[24px] tw-space-y-3">
                        {/* All Schemas card */}
                        <div
                            className={`tw-border tw-border-solid tw-rounded-xl tw-p-4 transition-card ${
                                selectedSchemaMode === "allSchemas" ? "border-primary shadow-sm" : "tw-border-[#E9EAEB] dark:tw-border-[#2B2B40]"
                            }`}
                        >
                            <div className="d-flex align-items-center">
                                <Form.Check
                                    custom
                                    type="radio"
                                    id="allSchemasRadio"
                                    name="schema_mode_selection_static"
                                    value="allSchemas"
                                    className="md:tw-mr-3"
                                    checked={selectedSchemaMode === "allSchemas"}
                                    onChange={() => handleSchemaModeChange("allSchemas")}
                                    disabled={isIntegrationContent}
                                />
                                <div>
                                    <h5 className="tw-mb-1 tw-font-[400] tw-text-black dark:tw-text-white tw-text-sm">
                                        <i className="tw-me-1">
                                            <Layers2 size={16} color="#A4A7AE" />
                                        </i>
                                        All {schemaLabel}s
                                    </h5>
                                    <p className="tw-mb-0 tw-text-[#535862] dark:tw-text-[#787B86] tw-text-xs">
                                        Process all available {schemaLabel.toLowerCase()}s automatically
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Specified Schemas card */}
                        <div
                            className={`tw-border tw-border-solid tw-rounded-xl tw-p-4 transition-card ${
                                selectedSchemaMode === "specificSchemas" ? "border-primary shadow-sm" : "tw-border-[#E9EAEB] dark:tw-border-[#2B2B40]"
                            }`}
                        >
                            <div className="d-flex align-items-center tw-pb-3">
                                <Form.Check
                                    custom
                                    type="radio"
                                    id="specificSchemasRadio"
                                    name="schema_mode_selection_static"
                                    value="specificSchemas"
                                    className="md:tw-mr-3"
                                    checked={selectedSchemaMode === "specificSchemas"}
                                    onChange={() => handleSchemaModeChange("specificSchemas")}
                                    disabled={isIntegrationContent}
                                />
                                <div>
                                    <h5 className="tw-mb-1 tw-font-[400] tw-text-black dark:tw-text-white tw-text-sm">
                                        <i className="tw-me-1">
                                            <FolderOpen size={16} color="#A4A7AE" />
                                        </i>
                                        Specified {schemaLabel}s
                                    </h5>
                                    <p className="tw-mb-0 tw-text-[#535862] dark:tw-text-[#787B86] tw-text-xs">
                                        Select specific {schemaLabel.toLowerCase()}s from your datasource
                                    </p>
                                </div>
                            </div>

                            {selectedSchemaMode === "specificSchemas" && (
                                <div className="tw-ps-5 md:tw-ps-8 tw-py-3 tw-border-0 tw-border-t tw-border-solid tw-border-[#E9EAEB] dark:tw-border-[#2B2B40] tw-space-y-3">
                                    {!isIntegrationContent && (
                                        <button
                                            type="button"
                                            className={confirmedSchemas.length > 0 && hasFetchedAvailableSchemas
                                                ? "btn !tw-border-[#ABEFC6] tw-bg-[#F6FEF9] tw-text-black btn-block tw-mb-3 hover:!tw-shadow-none tw-flex tw-items-center tw-justify-center"
                                                : "btn tw-bg-[#FAFAFA] tw-border tw-border-solid tw-border-[#D5D7DA] tw-text-[#484848] hover:tw-bg-[#F5F5F5] hover:tw-border-[#E9EAEC] btn-block tw-mb-3 hover:!tw-shadow-none tw-flex tw-items-center tw-justify-center dark:!tw-bg-[#2B2B40] dark:tw-border-[#3A3A55] dark:tw-text-white"
                                            }
                                            onClick={handleOpenSchemaModal}
                                        >
                                            <i className="tw-mr-1.5">
                                                <FolderTree size={16} color="#079455" />
                                            </i>
                                            <span className={confirmedSchemas.length > 0 && hasFetchedAvailableSchemas
                                                ? "tw-text-[#079455] tw-font-medium tw-text-xs"
                                                : "tw-text-[#484848] dark:tw-text-white tw-font-medium tw-text-xs"}>
                                                {isFetchingSchemas
                                                    ? `Fetching ${schemaLabel}s...`
                                                    : confirmedSchemas.length > 0 && hasFetchedAvailableSchemas
                                                        ? `Selected ${schemaLabel} (${confirmedSchemas.length})`
                                                        : `Fetch Available ${bucketDatasource ? "Buckets" : "Schemas"}`}
                                            </span>
                                        </button>
                                    )}
                                    {selectedSchemas.length > 0 && (
                                        <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mb-3">
                                            {selectedSchemas.map((schema) => (
                                                <span
                                                    key={schema}
                                                    className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-solid tw-border-[#C7D7FE] tw-bg-[#EEF4FF] tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-[#3538CD]"
                                                >
                                                    <FolderTree size={12} className="tw-text-[#175CD3]" />
                                                    {schema}
                                                    {pendingSchemaAdditions.includes(schema) && (
                                                        <span className="tw-ml-1 tw-text-[#067647]">will be added</span>
                                                    )}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {(pendingSchemaAdditions.length > 0 || pendingSchemaRemovals.length > 0) && (
                                        <div className="tw-flex tw-flex-wrap tw-gap-x-3 tw-gap-y-1 tw-mb-3 tw-text-xs" aria-live="polite">
                                            {pendingSchemaAdditions.length > 0 && (
                                                <span className="tw-font-medium tw-text-[#067647]">
                                                    {pendingSchemaAdditions.length} {schemaLabel.toLowerCase()}{pendingSchemaAdditions.length === 1 ? "" : "s"} will be added
                                                </span>
                                            )}
                                            {pendingSchemaRemovals.length > 0 && (
                                                <span
                                                    className="tw-font-medium tw-text-[#F04438]"
                                                    style={{ marginLeft: pendingSchemaAdditions.length > 0 ? 16 : 0 }}
                                                >
                                                    {pendingSchemaRemovals.length} {schemaLabel.toLowerCase()}{pendingSchemaRemovals.length === 1 ? "" : "s"} will be removed
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {schemaFetchError && (
                                        <div className="alert alert-danger tw-mb-3" role="alert">
                                            {schemaFetchError}
                                        </div>
                                    )}

                                    <div>
                                        <Form.Label className="tw-block tw-font-medium tw-mb-2">
                                            Configuration Mode <span className="text-danger">*</span>
                                        </Form.Label>
                                        <div className="custom-control custom-radio xl:tw-flex pl-0 tw-gap-6">
                                            <label className="d-flex align-items-center tw-mr-6 tw-mb-0 tw-cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="configModeStatic"
                                                    value="autoconfig"
                                                    className="mr-2"
                                                    checked={configMode === "autoconfig"}
                                                    onChange={() => handleConfigurationModeChange("autoconfig")}
                                                    disabled={isIntegrationContent}
                                                />
                                                Autoconfig
                                            </label>
                                            <label className="d-flex align-items-center tw-mb-0 tw-cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="configModeStatic"
                                                    value="manual"
                                                    className="mr-2"
                                                    checked={configMode === "manual"}
                                                    onChange={() => handleConfigurationModeChange("manual")}
                                                    disabled={isIntegrationContent || selectedSchemaMode !== "specificSchemas"}
                                                />
                                                Manual Config
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* 3. Manual/Integration Configuration Details */}
            <div className="tw-space-y-2">
                <div className="tw-sticky tw-top-[58px] tw-z-10 tw-bg-white dark:tw-bg-[#1E1E2D] tw-py-2 tw-flex tw-items-center tw-justify-between">
                    <button
                        type="button"
                        className="tw-flex tw-items-center tw-gap-1.5 tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer tw-px-1"
                        onClick={() => setIsManualConfigOpen(!isManualConfigOpen)}
                    >
                        {isManualConfigOpen ? (
                            <ChevronDown size={16} className="tw-text-[#007bff] dark:tw-text-[#007bff]" />
                        ) : (
                            <ChevronRight size={16} className="tw-text-[#007bff] dark:tw-text-[#007bff]" />
                        )}
                        <h6 className="!tw-m-0 tw-text-base tw-font-semibold tw-text-[#007bff] dark:tw-text-[#007bff]">
                            {isIntegrationContent
                                ? "Integration Configuration Details"
                                : configMode === "manual" ? "Manual Configuration Details" : "Autoconfig Integration Details"}
                        </h6>
                    </button>

                    {!isIntegrationContent && configMode === "manual" && (
                        <button
                            type="button"
                            ref={addIntegrationButtonRef}
                            className="btn btn-outline-primary tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs"
                            onClick={handleAddIntegration}
                            disabled={hasUnnamedIntegration}
                            title={hasUnnamedIntegration ? "Enter the integration name before adding another integration." : "Add Integration"}
                        >
                            <Plus size={14} />
                            Add Integration
                        </button>
                    )}
                </div>

                {isManualConfigOpen && showIntegrationConfigurations && (
                    <div className="tw-ml-[24px] tw-space-y-4">
                        {integrations.map((config, index) => {
                            const isConfigOpen = config.isOpen !== false;
                            const isIntegrationNameInvalid = !config.integrationName.trim() || hasDuplicateIntegrationName(config.id, config.integrationName);
                            const integrationNameError = !config.integrationName.trim()
                                ? "Integration name is required."
                                : "An integration with this name already exists.";
                            const isExistingDatasourceIntegrationReadOnly = (
                                !isIntegrationContent &&
                                config.isPersisted &&
                                !isManualAfterAutoTransition
                            );
                            const canEditSchemaAndDatasetScope = !isIntegrationContent ||
                                config.incrementalData.some((item) => !item.isPersisted);
                            return (
                                <div
                                    key={config.id}
                                    ref={config.id === integrationFocusId ? newIntegrationRef : null}
                                    tabIndex={config.id === integrationFocusId ? -1 : undefined}
                                    className="tw-border-2 tw-border-solid tw-border-[#9BCBFF] tw-px-4 tw-rounded-xl tw-bg-white dark:tw-bg-[#1E1E2D]"
                                >
                                    {/* Integration Header */}
                                    <div className={`tw-flex tw-items-center tw-justify-between tw-py-3 ${isConfigOpen ? "tw-border-0 tw-border-b tw-border-solid tw-border-[#E9EAEB] dark:tw-border-[#2B2B40]" : ""}`}>
                                        <div className="tw-flex tw-items-center tw-gap-2">
                                            <button
                                                type="button"
                                                className="tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer tw-flex tw-items-center tw-mr-1"
                                                onClick={() => handleToggleIntegrationOpen(config.id)}
                                            >
                                                {isConfigOpen ? (
                                                    <ChevronDown size={16} className="tw-text-[#717680] dark:tw-text-[#A1A5B7]" />
                                                ) : (
                                                    <ChevronRight size={16} className="tw-text-[#717680] dark:tw-text-[#A1A5B7]" />
                                                )}
                                            </button>
                                            <div className="tw-flex tw-items-center tw-gap-2">
                                                <Blocks size={18} color="#A4A7AE" />
                                                <div className="tw-flex tw-flex-col">
                                                    {config.isNameEditing ? (
                                                        <Form.Control
                                                            type="text"
                                                            aria-label="Integration name"
                                                            placeholder="Integration name"
                                                            ref={config.id === integrationFocusId ? newIntegrationNameRef : null}
                                                            className={`form-control form-control-sm !tw-w-64${isIntegrationNameInvalid ? " is-invalid" : ""}`}
                                                            value={config.integrationName}
                                                            onChange={(e) => handleIntegrationNameChange(config.id, e.target.value)}
                                                            onBlur={() => handleToggleNameEdit(config.id, false)}
                                                        />
                                                    ) : (
                                                        <div className="tw-flex tw-items-center tw-gap-2">
                                                            <h5 className="tw-font-medium tw-text-black dark:tw-text-[#B2B5BE] tw-m-0 tw-text-sm">
                                                                {config.integrationName}
                                                            </h5>
                                                            <button
                                                                type="button"
                                                                className="tw-p-1 tw-bg-transparent tw-border-0 tw-text-[#A4A7AE] hover:tw-text-[#475467] tw-cursor-pointer"
                                                                aria-label="Edit integration name"
                                                                title="Edit integration name"
                                                                onClick={() => handleToggleNameEdit(config.id, true)}
                                                            >
                                                                <Pencil size={16} />
                                                            </button>
                                                        </div>
                                                    )}
                                                    {!config.isNameEditing && isIntegrationNameInvalid && (
                                                        <div className="invalid-feedback d-block">{integrationNameError}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {!isIntegrationContent && integrations.length > 1 && (
                                            <button
                                                type="button"
                                                className="tw-border-0 tw-bg-transparent text-primary tw-text-xs tw-cursor-pointer"
                                                onClick={() => handleRemoveIntegration(config.id)}
                                            >
                                                <Trash2 size={16} color="currentColor" className="mr-1" />
                                                <span className="tw-hidden sm:tw-inline-block">Remove</span>
                                            </button>
                                        )}
                                    </div>

                                    {/* Integration Body */}
                                    {isConfigOpen && (
                                        <div className="tw-py-4">
                                    <fieldset
                                        disabled={isExistingDatasourceIntegrationReadOnly}
                                        className="tw-min-w-0 tw-border-0 tw-m-0 tw-p-0"
                                    >
                                    {/* Select Schema */}
                                    <div className="row">
                                        <div className="col-md-6 !tw-border-0">
                                            <Form.Label className="tw-font-medium">
                                                Select {schemaLabel} <span className="text-primary">*</span>
                                            </Form.Label>
                                            <Select
                                                id={`select_schema_${config.id}`}
                                                options={schemaOptions}
                                                placeholder={`Select ${schemaLabel}`}
                                                value={schemaOptions.find(s => s.value === config.schema) || null}
                                                onChange={(opt) => handleSchemaSelect(config.id, opt)}
                                                classNamePrefix="select-control"
                                                components={{ Control: customSelectSchema }}
                                                isDisabled={isExistingDatasourceIntegrationReadOnly || !canEditSchemaAndDatasetScope}
                                            />
                                        </div>
                                    </div>

                                    {bucketDatasource && (
                                        <div className="row tw-mt-3">
                                            <Form.Group className="col-md-6">
                                                <Form.Label className="tw-font-medium">Subpath</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="sub_path"
                                                    placeholder="Subpath"
                                                    value={config.sub_path || ""}
                                                    onChange={(event) => handleIntegrationFieldChange(config.id, "sub_path", event.target.value)}
                                                />
                                            </Form.Group>
                                        </div>
                                    )}

                                    <hr className="tw-my-3 tw-border-0 tw-border-b tw-border-solid tw-border-[#E9EAEB] dark:tw-border-[#2B2B40]" />

                                    {/* Dataset Scope */}
                                    {!bucketDatasource && (
                                        <div className="row tw-mb-4">
                                            <Form.Group className="col-md-6 !tw-border-0">
                                                <Form.Label className="tw-font-medium">Dataset Scope</Form.Label>
                                                <div>
                                                    <div className="custom-control custom-radio custom-control-inline mt-1">
                                                        <input
                                                            type="radio"
                                                            id={`allDataset_${config.id}`}
                                                            name={`datasetGroup_${config.id}`}
                                                            value="allDatasets"
                                                            checked={config.datasetScope === "allDatasets"}
                                                            onChange={() => handleDatasetScopeChange(config.id, "allDatasets")}
                                                            className="custom-control-input"
                                                            disabled={isExistingDatasourceIntegrationReadOnly || !canEditSchemaAndDatasetScope}
                                                        />
                                                        <Form.Label className="custom-control-label" htmlFor={`allDataset_${config.id}`}>
                                                            All Datasets
                                                        </Form.Label>
                                                    </div>

                                                    <div className="custom-control custom-radio custom-control-inline mt-1">
                                                        <input
                                                            type="radio"
                                                            id={`specifiedDataset_${config.id}`}
                                                            name={`datasetGroup_${config.id}`}
                                                            value="specifiedDatasets"
                                                            checked={config.datasetScope === "specifiedDatasets"}
                                                            onChange={() => handleDatasetScopeChange(config.id, "specifiedDatasets")}
                                                            className="custom-control-input"
                                                            disabled={isExistingDatasourceIntegrationReadOnly || !canEditSchemaAndDatasetScope}
                                                        />
                                                        <Form.Label className="custom-control-label" htmlFor={`specifiedDataset_${config.id}`}>
                                                            Specified Datasets
                                                        </Form.Label>
                                                    </div>
                                                </div>
                                            </Form.Group>
                                        </div>
                                    )}

                                    {config.datasetScope === "specifiedDatasets" && config.incrementalData.length === 0 && (
                                        <div className="alert alert-danger tw-text-sm tw-py-2" role="alert">
                                            Add at least one dataset when using Specified Datasets.
                                        </div>
                                    )}

                                    {/* Add Incremental Data Identification Box */}
                                    <div className="tw-mb-4 tw-p-3 sm:tw-p-4 tw-border tw-border-solid tw-border-[#D5D7DA] dark:tw-border-[#2B2B40] tw-rounded-lg tw-bg-[#FAFAFA] dark:tw-bg-[#161622]">
                                        <div className="sm:tw-flex tw-items-center tw-justify-between">
                                            <div className="tw-flex tw-items-center tw-gap-2">
                                                <button
                                                    type="button"
                                                    className="tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer tw-flex tw-items-center"
                                                    onClick={() => handleToggleIncrementalOpen(config.id)}
                                                >
                                                    {config.isIncrementalOpen !== false ? (
                                                        <ChevronDown size={16} className="tw-text-[#717680] dark:tw-text-[#A1A5B7]" />
                                                    ) : (
                                                        <ChevronRight size={16} className="tw-text-[#717680] dark:tw-text-[#A1A5B7]" />
                                                    )}
                                                </button>
                                                <div className="tw-cursor-pointer" onClick={() => handleToggleIncrementalOpen(config.id)}>
                                                    <h5 className="tw-text-sm tw-font-medium tw-text-[#334DB7] tw-m-0">
                                                        Add Incremental Data Identification
                                                        {config.datasetScope === 'specifiedDatasets' ? (
                                                            <span className="text-danger"> *</span>
                                                        ) : (
                                                            <span className="tw-text-[#6B7280]"> (Optional)</span>
                                                        )}
                                                    </h5>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn tw-mt-3 sm:tw-mt-0 tw-font-semibold tw-border-0 tw-bg-[#E6E9F6] tw-text-[#334DB7] tw-whitespace-nowrap hover:tw-bg-[#D2D7EF] hover:tw-text-[#334DB7] tw-text-xs tw-inline-flex tw-items-center"
                                                onClick={() => handleAddIncremental(config.id)}
                                            >
                                                <Plus size={18} color="#8A99D6" className="mr-1" />
                                                Add Row
                                            </button>
                                        </div>

                                        {/* Incremental List (One by one flow) */}
                                        {config.isIncrementalOpen !== false && (
                                            <div className={`integration-collapse-content ${config.incrementalData.length > 0 ? "open tw-mt-3" : ""}`}>
                                                {config.incrementalData.map((incItem, incIndex) => {
                                                    const isIncItemOpen = incItem.isOpen !== false;
                                                    return (
                                                        <div key={incItem.id} className="dataset-partition-card tw-mb-3">
                                                            <div className="dp-card__head d-flex align-items-center justify-content-between tw-px-3 tw-py-2">
                                                                <div className="tw-flex tw-items-center tw-gap-2">
                                                                    <button
                                                                        type="button"
                                                                        className="tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer tw-flex tw-items-center"
                                                                        onClick={() => handleToggleIncItemOpen(config.id, incItem.id)}
                                                                    >
                                                                        {isIncItemOpen ? (
                                                                            <ChevronDown size={14} className="tw-text-[#717680] dark:tw-text-[#A1A5B7]" />
                                                                        ) : (
                                                                            <ChevronRight size={14} className="tw-text-[#717680] dark:tw-text-[#A1A5B7]" />
                                                                        )}
                                                                    </button>
                                                                    <span
                                                                        className="dp-card__label tw-font-semibold tw-text-xs tw-cursor-pointer"
                                                                        onClick={() => handleToggleIncItemOpen(config.id, incItem.id)}
                                                                    >
                                                                        Incremental Data {incIndex + 1}
                                                                    </span>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    className={`tw-border-0 tw-bg-transparent text-primary tw-text-xs ${config.datasetScope === "specifiedDatasets" && config.incrementalData.length <= 1 ? "tw-opacity-50 tw-cursor-not-allowed" : "tw-cursor-pointer"}`}
                                                                    onClick={() => handleRemoveIncremental(config.id, incItem.id)}
                                                                    disabled={config.datasetScope === "specifiedDatasets" && config.incrementalData.length <= 1}
                                                                    title={config.datasetScope === "specifiedDatasets" && config.incrementalData.length <= 1 ? "At least one dataset must remain configured" : "Remove dataset"}
                                                                >
                                                                    <Trash2 size={15} color="currentColor" className="mr-1" />
                                                                    <span className="tw-hidden sm:tw-inline-block">Remove</span>
                                                                </button>
                                                            </div>
                                                            {isIncItemOpen && (
                                                                <React.Fragment>
                                                                    <hr className="tw-border-0 tw-border-b tw-border-solid tw-border-[#E9EAEB] dark:tw-border-[#2B2B40] tw-m-0" />
                                                                    <div className="dp-card__content tw-p-3">
                                                                        {/* Row 1 */}
                                                                        <div className="form-row">
                                                                            {/* Dataset Name */}
                                                                            <div className="form-group col-md-6">
                                                                                <label className="tw-font-medium">
                                                                                    Dataset Name <span className="text-danger">*</span>
                                                                                </label>
                                                                                <div className="icon-input-group">
                                                                                    <i>
                                                                                        <Layers size={18} color="#A4A7AE" />
                                                                                    </i>
                                                                                    <Form.Control
                                                                                        id={`dataset-input-${config.id}-${incItem.id}`}
                                                                                        type="text"
                                                                                        className={`form-control form-control-sm${!incItem.datasetName.trim() || integrationValidationErrors[config.id]?.datasets?.[incItem.id] ? " is-invalid" : ""}`}
                                                                                        name="dataset_name"
                                                                                        placeholder="Dataset Name"
                                                                                        value={incItem.datasetName}
                                                                                        onChange={(e) => handleIncrementalFieldChange(config.id, incItem.id, 'datasetName', e.target.value)}
                                                                                        disabled={isExistingDatasourceIntegrationReadOnly}
                                                                                    />
                                                                                    {hasDuplicateDataset(config.id, config.schema, incItem.datasetName) && (
                                                                                        <div className="alert alert-warning tw-text-xs tw-py-2 tw-mt-2" role="status">
                                                                                            This dataset is already configured in another integration for this schema.
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                                {!incItem.datasetName.trim() && (
                                                                                    <div className="invalid-feedback d-block">
                                                                                        Dataset Name is required.
                                                                                    </div>
                                                                                )}
                                                                                {integrationValidationErrors[config.id]?.datasets?.[incItem.id] && (
                                                                                    <div className="invalid-feedback d-block">
                                                                                        {integrationValidationErrors[config.id].datasets[incItem.id]}
                                                                                    </div>
                                                                                )}
                                                                                <Form.Text className="text-muted tw-mt-1">
                                                                                    <Form.Check
                                                                                        custom
                                                                                        type="checkbox"
                                                                                        label="Exact match only"
                                                                                        id={`exact-match_${config.id}_${incItem.id}`}
                                                                                        checked={incItem.exactMatch}
                                                                                        onChange={(e) => handleIncrementalFieldChange(config.id, incItem.id, 'exactMatch', e.target.checked)}
                                                                                    />
                                                                                </Form.Text>
                                                                            </div>

                                                                            {/* Incremental Data Identifier */}
                                                                            <div className="form-group col-md-6">
                                                                                <label className="tw-font-medium">
                                                                                    Incremental Data Identifier {config.datasetScope !== "specifiedDatasets" && <span className="text-danger">*</span>}
                                                                                </label>
                                                                                <div className="icon-input-group">
                                                                                    <i>
                                                                                        <HardDrive size={18} color="#A4A7AE" />
                                                                                    </i>
                                                                                    <Form.Control
                                                                                        type="text"
                                                                                        className={`form-control form-control-sm${config.datasetScope !== "specifiedDatasets" && !incItem.identifier.trim() ? " is-invalid" : ""}`}
                                                                                        name="incremental_identifier"
                                                                                        placeholder="Incremental data identifier"
                                                                                        value={incItem.identifier}
                                                                                        onChange={(e) => handleIncrementalFieldChange(config.id, incItem.id, 'identifier', e.target.value)}
                                                                                    />
                                                                                </div>
                                                                                {config.datasetScope !== "specifiedDatasets" && !incItem.identifier.trim() && (
                                                                                    <div className="invalid-feedback d-block">
                                                                                        Incremental Data Identifier is required.
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>

                                                                        {/* Row 2 */}
                                                                        <div className="form-row tw-mt-2">
                                                                            {/* Starting Index (Optional) */}
                                                                            <div className="form-group col-md-6">
                                                                                <div className="custom-control custom-checkbox mb-2">
                                                                                    <Form.Control
                                                                                        id={`start_index_check_box_${config.id}_${incItem.id}`}
                                                                                        type="checkbox"
                                                                                        checked={incItem.startingIndexCheckbox}
                                                                                        className="custom-control-input"
                                                                                        onChange={(e) => handleIncrementalFieldChange(config.id, incItem.id, 'startingIndexCheckbox', e.target.checked)}
                                                                                    />
                                                                                    <Form.Label className="custom-control-label" htmlFor={`start_index_check_box_${config.id}_${incItem.id}`}>
                                                                                        Starting Index (Optional)
                                                                                    </Form.Label>
                                                                                </div>
                                                                                <div>
                                                                                    <div className="custom-control custom-radio">
                                                                                        <input
                                                                                            type="radio"
                                                                                            className="custom-control-input"
                                                                                            id={`radio_by_value_${config.id}_${incItem.id}`}
                                                                                            name={`start_index_${config.id}_${incItem.id}`}
                                                                                            value="incremental data identifier"
                                                                                            checked={incItem.startingIndexType === 'incremental data identifier'}
                                                                                            disabled={!incItem.startingIndexCheckbox}
                                                                                            onChange={() => handleIncrementalFieldChange(config.id, incItem.id, 'startingIndexType', 'incremental data identifier')}
                                                                                        />
                                                                                        <label className="custom-control-label" htmlFor={`radio_by_value_${config.id}_${incItem.id}`}>
                                                                                            By Value
                                                                                        </label>
                                                                                    </div>
                                                                                    <div className="custom-control custom-radio">
                                                                                        <input
                                                                                            type="radio"
                                                                                            className="custom-control-input"
                                                                                            id={`radio_by_query_${config.id}_${incItem.id}`}
                                                                                            name={`start_index_${config.id}_${incItem.id}`}
                                                                                            value="custom query"
                                                                                            checked={incItem.startingIndexType === 'custom query'}
                                                                                            disabled={!incItem.startingIndexCheckbox}
                                                                                            onChange={() => handleIncrementalFieldChange(config.id, incItem.id, 'startingIndexType', 'custom query')}
                                                                                        />
                                                                                        <label className="custom-control-label" htmlFor={`radio_by_query_${config.id}_${incItem.id}`}>
                                                                                            By Query
                                                                                        </label>
                                                                                    </div>
                                                                                </div>
                                                                            </div>

                                                                            {/* Dynamic Starting Index Value Input */}
                                                                            {incItem.startingIndexCheckbox && incItem.startingIndexType === 'incremental data identifier' && (
                                                                                <Form.Group className="col-md-6">
                                                                                    <Form.Label className="tw-font-medium">Starting Index Value</Form.Label>
                                                                                    <Form.Control
                                                                                        type="text"
                                                                                        className={`form-control form-control-sm${incItem.startingIndexCheckbox && incItem.startingIndexType === "incremental data identifier" && !incItem.startingIndexValue.trim() ? " is-invalid" : ""}`}
                                                                                        name="start_index_value"
                                                                                        placeholder="Starting Index Value"
                                                                                        value={incItem.startingIndexValue}
                                                                                        onChange={(e) => handleIncrementalFieldChange(config.id, incItem.id, 'startingIndexValue', e.target.value)}
                                                                                    />
                                                                                    {!incItem.startingIndexValue.trim() && (
                                                                                        <div className="invalid-feedback d-block">Starting Index Value is required.</div>
                                                                                    )}
                                                                                </Form.Group>
                                                                            )}

                                                                            {/* Dynamic Starting Index Query Input */}
                                                                            {incItem.startingIndexCheckbox && incItem.startingIndexType === 'custom query' && (
                                                                                <Form.Group className="col-md-6">
                                                                                    <Form.Label className="tw-font-medium">Starting Index Query</Form.Label>
                                                                                    <Form.Control
                                                                                        as="textarea"
                                                                                        rows={2}
                                                                                        name="start_index_query"
                                                                                        className={`form-control form-control-sm${!incItem.startingIndexQuery.trim() ? " is-invalid" : ""}`}
                                                                                        placeholder="Starting Index Query"
                                                                                        value={incItem.startingIndexQuery}
                                                                                        onChange={(e) => handleIncrementalFieldChange(config.id, incItem.id, 'startingIndexQuery', e.target.value)}
                                                                                    />
                                                                                    {!incItem.startingIndexQuery.trim() && (
                                                                                        <div className="invalid-feedback d-block">Starting Index query is required.</div>
                                                                                    )}
                                                                                    <Form.Text className="text-muted tw-text-[11px]">
                                                                                        <strong className="mr-1">Note:</strong>This query will be used for Starting Index.
                                                                                    </Form.Text>
                                                                                </Form.Group>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </React.Fragment>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                        )}
                                    </div>

                                    {!bucketDatasource && (<>
                                    {/* Add Partitions Box */}
                                    <div className="tw-p-3 sm:tw-p-4 tw-border tw-border-solid tw-border-[#D5D7DA] dark:tw-border-[#2B2B40] tw-rounded-lg tw-bg-[#FAFAFA] dark:tw-bg-[#161622]">
                                        <div className="sm:tw-flex tw-items-center tw-justify-between">
                                            <div className="tw-flex tw-items-center tw-gap-2">
                                                <button
                                                    type="button"
                                                    className="tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer tw-flex tw-items-center"
                                                    onClick={() => handleTogglePartitionsOpen(config.id)}
                                                >
                                                    {config.isPartitionsOpen !== false ? (
                                                        <ChevronDown size={16} className="tw-text-[#717680] dark:tw-text-[#A1A5B7]" />
                                                    ) : (
                                                        <ChevronRight size={16} className="tw-text-[#717680] dark:tw-text-[#A1A5B7]" />
                                                    )}
                                                </button>
                                                <div className="tw-cursor-pointer" onClick={() => handleTogglePartitionsOpen(config.id)}>
                                                    <h5 className="tw-text-sm tw-font-medium tw-text-[#334DB7] tw-m-0">Add Partitions</h5>
                                                    <p className="tw-text-xs tw-text-[#546AC3] tw-m-0">Configure partitioning dataset.</p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn tw-mt-3 sm:tw-mt-0 tw-font-semibold tw-border-0 tw-bg-[#E6E9F6] tw-text-[#334DB7] tw-whitespace-nowrap hover:tw-bg-[#D2D7EF] hover:tw-text-[#334DB7] tw-text-xs tw-inline-flex tw-items-center"
                                                onClick={() => handleAddPartition(config.id)}
                                            >
                                                <Plus size={18} color="#8A99D6" className="mr-1" />
                                                Add Row
                                            </button>
                                        </div>

                                        {/* Partitions List (One by one flow) */}
                                        {config.isPartitionsOpen !== false && (
                                            <div className={`integration-collapse-content ${config.partitions.length > 0 ? "open tw-mt-3" : ""}`}>
                                            {config.partitions.map((partition, partIndex) => {
                                                const isPartItemOpen = partition.isOpen !== false;
                                                const hasDuplicateDataset = hasDuplicatePartitionDataset(config, partition.id, partition.datasetName);
                                                return (
                                                    <div key={partition.id} className="dataset-partition-card tw-mb-3">
                                                        <div className="dp-card__head d-flex align-items-center justify-content-between tw-px-3 tw-py-2">
                                                            <div className="tw-flex tw-items-center tw-gap-2">
                                                                <button
                                                                    type="button"
                                                                    className="tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer tw-flex tw-items-center"
                                                                    onClick={() => handleTogglePartItemOpen(config.id, partition.id)}
                                                                >
                                                                    {isPartItemOpen ? (
                                                                        <ChevronDown size={14} className="tw-text-[#717680] dark:tw-text-[#A1A5B7]" />
                                                                    ) : (
                                                                        <ChevronRight size={14} className="tw-text-[#717680] dark:tw-text-[#A1A5B7]" />
                                                                    )}
                                                                </button>
                                                                <span
                                                                    className="dp-card__label tw-font-semibold tw-text-xs tw-cursor-pointer"
                                                                    onClick={() => handleTogglePartItemOpen(config.id, partition.id)}
                                                                >
                                                                    Partition {partIndex + 1}
                                                                </span>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="tw-border-0 tw-bg-transparent text-primary tw-text-xs tw-cursor-pointer"
                                                                onClick={() => handleRemovePartition(config.id, partition.id)}
                                                            >
                                                                <Trash2 size={15} color="currentColor" className="mr-1" />
                                                                <span className="tw-hidden sm:tw-inline-block">Remove</span>
                                                            </button>
                                                        </div>
                                                        {isPartItemOpen && (
                                                            <React.Fragment>
                                                                <hr className="tw-border-0 tw-border-b tw-border-solid tw-border-[#E9EAEB] dark:tw-border-[#2B2B40] tw-m-0" />
                                                                <div className="dp-card__content tw-p-3">
                                                                    {/* Row 1 */}
                                                                    <div className="form-row">
                                                                        {/* Dataset Name */}
                                                                        <Form.Group className="col-md-6">
                                                                            <Form.Label className="tw-font-medium">Dataset Name <span className="text-danger">*</span></Form.Label>
                                                                            {config.datasetScope === "allDatasets" ? (
                                                                                <Form.Control
                                                                                    type="text"
                                                                                    name="dataset_name"
                                                                                    placeholder="Dataset Name"
                                                                                    className={!partition.datasetName.trim() || hasDuplicateDataset ? "is-invalid" : ""}
                                                                                    value={partition.datasetName}
                                                                                    onChange={(event) => handlePartitionFieldChange(config.id, partition.id, "datasetName", event.target.value)}
                                                                                    disabled={isExistingDatasourceIntegrationReadOnly}
                                                                                />
                                                                            ) : (
                                                                                <Select
                                                                                    name="dataset_name"
                                                                                    options={getPartitionDatasetOptions(config, partition.id)}
                                                                                    placeholder="Select an existing dataset"
                                                                                    value={partition.datasetName
                                                                                        ? { label: partition.datasetName, value: partition.datasetName }
                                                                                        : null}
                                                                                    onChange={(option) => handlePartitionFieldChange(config.id, partition.id, "datasetName", option ? option.value : "")}
                                                                                    classNamePrefix="select-control"
                                                                                    isDisabled={isExistingDatasourceIntegrationReadOnly}
                                                                                    isOptionDisabled={(option) => option.isDisabled}
                                                                                />
                                                                            )}
                                                                            {!partition.datasetName.trim() && (
                                                                                <div className="invalid-feedback d-block">Dataset Name is required.</div>
                                                                            )}
                                                                            {hasDuplicateDataset && (
                                                                                <div className="invalid-feedback d-block">Each dataset can have only one partition.</div>
                                                                            )}
                                                                        </Form.Group>

                                                                        {/* Partition Name */}
                                                                        <Form.Group className="col-md-6">
                                                                            <Form.Label className="tw-font-medium">Partition Name <span className="text-danger">*</span></Form.Label>
                                                                            <div className="icon-input-group">
                                                                                <i>
                                                                                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                        <g clipPath={`clip0_part_${config.id}_${partition.id}`}>
                                                                                            <path d="M1.33464 10.668C1.70249 10.669 1.9993 10.9681 1.9987 11.3359L6.66927 13.4596C6.68835 13.8731 6.80116 14.2621 6.98763 14.6055C7.02875 14.6812 7.07374 14.7545 7.12175 14.8255C7.23043 14.9864 7.35652 15.1343 7.49675 15.2676C7.38779 15.2391 7.28089 15.2023 7.17774 15.1556L7.17709 15.1549L1.44336 12.5482L1.43946 12.5469C1.20812 12.4398 1.01206 12.2683 0.875003 12.0534C0.737984 11.8384 0.66544 11.5883 0.666019 11.3333C0.66693 10.9651 0.966453 10.6671 1.33464 10.668Z" fill="#A4A7AE"/>
                                                                                            <path d="M1.33464 7.33464C1.70249 7.33571 1.9993 7.63478 1.9987 8.0026L7.66602 10.5794V11.2552C7.46838 11.4137 7.29376 11.5999 7.14779 11.8079L1.44336 9.21484L1.43946 9.21354C1.20812 9.10648 1.01206 8.93497 0.875003 8.72005C0.737984 8.50507 0.66544 8.25493 0.666019 8C0.66693 7.63181 0.966453 7.33373 1.33464 7.33464Z" fill="#A4A7AE"/>
                                                                                            <path d="M7.99935 0.667969C8.28574 0.667969 8.56887 0.729456 8.82943 0.848307L8.82878 0.848958L14.5553 3.45508C14.789 3.55966 14.9877 3.72879 15.1276 3.94336C15.2687 4.15983 15.3437 4.41286 15.3438 4.67122C15.3437 4.92969 15.2687 5.1832 15.1276 5.39974C14.9878 5.6142 14.7883 5.7828 14.5547 5.88737L14.5553 5.88867L12.6335 6.76172C12.3535 6.83762 12.0921 6.95782 11.8568 7.11458L10.8177 7.58659C10.5786 7.11122 10.1211 6.76654 9.57487 6.68685L14.0104 4.67122L14.0033 4.66862L8.2767 2.06185L8.27604 2.0612C8.18921 2.0216 8.09479 2.0013 7.99935 2.0013C7.90391 2.0013 7.80949 2.0216 7.72266 2.0612L7.72201 2.06185L2.00196 4.66471L7.72917 7.27474C7.81398 7.31342 7.90619 7.33368 7.99935 7.33464C7.7901 7.6132 7.66602 7.95941 7.66602 8.33464V8.63867C7.4973 8.60958 7.33235 8.55969 7.17578 8.48828L1.45638 5.88151C1.22317 5.77693 1.02447 5.6074 0.884769 5.39323C0.743577 5.17666 0.668655 4.92324 0.668623 4.66471C0.668654 4.40628 0.743669 4.15337 0.884769 3.93685C1.02453 3.72248 1.22295 3.55252 1.45638 3.44792L7.16927 0.848958C7.42983 0.730108 7.71297 0.66797 7.99935 0.667969Z" fill="#A4A7AE"/>
                                                                                            <path d="M13.9994 9.33464C13.9994 8.96645 13.7009 8.66797 13.3327 8.66797C12.9645 8.66797 12.666 8.96645 12.666 9.33464C12.666 9.70283 12.9645 10.0013 13.3327 10.0013C13.7009 10.0013 13.9994 9.70283 13.9994 9.33464ZM8.66602 13.3346C8.66602 13.7028 8.9645 14.0013 9.33269 14.0013C9.70088 14.0013 9.99936 13.7028 9.99936 13.3346C9.99936 12.9664 9.70088 12.668 9.33269 12.668C8.9645 12.668 8.66602 12.9664 8.66602 13.3346ZM14.666 9.33464C14.666 9.9609 14.2341 10.4858 13.652 10.6289C13.5832 11.4051 13.2451 12.1364 12.6898 12.6917C12.1344 13.2471 11.4031 13.5852 10.627 13.654C10.4838 14.2361 9.95895 14.668 9.33269 14.668C8.59631 14.668 7.99936 14.071 7.99936 13.3346C7.99936 12.7134 8.42428 12.1913 8.99936 12.0433V8.33464C8.99936 8.15054 9.14859 8.0013 9.33269 8.0013C9.51678 8.0013 9.66602 8.15054 9.66602 8.33464V12.0433C10.1292 12.1625 10.4947 12.5244 10.6195 12.9854C11.2212 12.9203 11.7864 12.6524 12.2184 12.2204C12.6505 11.7884 12.9183 11.2232 12.9834 10.6214C12.4165 10.4679 11.9994 9.95013 11.9994 9.33464C11.9994 8.59826 12.5963 8.0013 13.3327 8.0013C14.0691 8.0013 14.666 8.59826 14.666 9.33464Z" fill="#A4A7AE"/>
                                                                                        </g>
                                                                                        <defs>
                                                                                            <clipPath id={`clip0_part_${config.id}_${partition.id}`}>
                                                                                                <rect width="16" height="16" fill="white"/>
                                                                                            </clipPath>
                                                                                        </defs>
                                                                                    </svg>
                                                                                </i>
                                                                                <Form.Control
                                                                                    type="text"
                                                                                    className={`form-control form-control-sm${!partition.partitionName.trim() ? " is-invalid" : ""}`}
                                                                                    name="partition_name"
                                                                                    placeholder="Partition Name"
                                                                                    value={partition.partitionName}
                                                                                    onChange={(e) => handlePartitionFieldChange(config.id, partition.id, 'partitionName', e.target.value)}
                                                                                />
                                                                                {!partition.partitionName.trim() && (
                                                                                    <div className="invalid-feedback d-block">Partition Name is required.</div>
                                                                                )}
                                                                            </div>
                                                                        </Form.Group>
                                                                    </div>

                                                                    {/* Row 2 */}
                                                                    <div className="form-row tw-mt-2">
                                                                        {/* Partition By */}
                                                                        <Form.Group className="col-md-6">
                                                                            <Form.Label className="d-block tw-font-medium">Partition By</Form.Label>
                                                                            <div className="custom-control custom-radio custom-control-inline mt-1">
                                                                                <input
                                                                                    type="radio"
                                                                                    className="custom-control-input"
                                                                                    id={`partitionByColumn_${config.id}_${partition.id}`}
                                                                                    name={`partitionBy_${config.id}_${partition.id}`}
                                                                                    value="Column"
                                                                                    checked={partition.partitionBy === 'Column'}
                                                                                    onChange={() => handlePartitionFieldChange(config.id, partition.id, 'partitionBy', 'Column')}
                                                                                />
                                                                                <Form.Label className="custom-control-label" htmlFor={`partitionByColumn_${config.id}_${partition.id}`}>
                                                                                    Column
                                                                                </Form.Label>
                                                                            </div>
                                                                            <div className="custom-control custom-radio custom-control-inline mt-1">
                                                                                <input
                                                                                    type="radio"
                                                                                    className="custom-control-input"
                                                                                    id={`partitionByCustom_${config.id}_${partition.id}`}
                                                                                    name={`partitionBy_${config.id}_${partition.id}`}
                                                                                    value="Custom Query"
                                                                                    checked={partition.partitionBy === 'Custom Query'}
                                                                                    onChange={() => handlePartitionFieldChange(config.id, partition.id, 'partitionBy', 'Custom Query')}
                                                                                />
                                                                                <Form.Label className="custom-control-label" htmlFor={`partitionByCustom_${config.id}_${partition.id}`}>
                                                                                    Custom Query
                                                                                </Form.Label>
                                                                            </div>
                                                                        </Form.Group>

                                                                        {/* Dynamic Partition Column Identifier Input */}
                                                                        {partition.partitionBy === 'Column' && (
                                                                            <Form.Group className="col-md-6">
                                                                                <Form.Label className="tw-font-medium">Partition Identifier</Form.Label>
                                                                                <div className="icon-input-group">
                                                                                    <i>
                                                                                        <TextSearch size={18} color="#A4A7AE" />
                                                                                    </i>
                                                                                    <Form.Control
                                                                                        type="text"
                                                                                        className={`form-control form-control-sm${!partition.partitionIdentifier.trim() ? " is-invalid" : ""}`}
                                                                                        name="partition_identifier"
                                                                                        placeholder="Partition Identifier"
                                                                                        value={partition.partitionIdentifier}
                                                                                        onChange={(e) => handlePartitionFieldChange(config.id, partition.id, 'partitionIdentifier', e.target.value)}
                                                                                    />
                                                                                    {!partition.partitionIdentifier.trim() && (
                                                                                        <div className="invalid-feedback d-block">Partition Identifier is required.</div>
                                                                                    )}
                                                                                </div>
                                                                            </Form.Group>
                                                                        )}

                                                                        {/* Dynamic Custom Query Textarea Input */}
                                                                        {partition.partitionBy === 'Custom Query' && (
                                                                            <Form.Group className="col-md-6">
                                                                                <Form.Label className="tw-font-medium">Custom Query <span className="text-danger">*</span></Form.Label>
                                                                                <Form.Control
                                                                                    as="textarea"
                                                                                    rows={2}
                                                                                    name="custom_query"
                                                                                    className={`form-control form-control-sm${!partition.customQuery.trim() ? " is-invalid" : ""}`}
                                                                                    placeholder="Enter your custom query here..."
                                                                                    value={partition.customQuery}
                                                                                    onChange={(e) => handlePartitionFieldChange(config.id, partition.id, 'customQuery', e.target.value)}
                                                                                />
                                                                                {!partition.customQuery.trim() && (
                                                                                    <div className="invalid-feedback d-block">Custom Query is required.</div>
                                                                                )}
                                                                            </Form.Group>
                                                                        )}
                                                                        </div>
                                                                        </div>
                                                            </React.Fragment>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        )}
                                    </div>
                                    </>)}
                                        </fieldset>
                                </div>
                            )}
                        </div>
                    );
                })}
                    </div>
                )}
            </div>
        </div>
        <Modal
            show={showSchemaModal}
            onHide={() => setShowSchemaModal(false)}
            centered
            size="md"
            className="modal-integration"
        >
            <Modal.Header closeButton>
                <div>
                    <h5 className="modal-title !tw-mb-[2px] tw-font-semibold !tw-text-gray-900 dark:!tw-text-white">
                        Select {schemaLabel}s
                    </h5>
                    <p className="tw-text-sm tw-text-[#535862] dark:tw-text-[#A1A5B7] tw-m-0">
                        {selectedSchemas.length} of {availableSchemaNames.length} selected
                    </p>
                </div>
            </Modal.Header>
            <Modal.Body>
                <div className="tw-space-y-3">
                    {isFetchingSchemas ? (
                        <div className="tw-py-[60px] tw-text-center" role="status">
                            <i className="tw-p-4 tw-rounded-full tw-bg-[linear-gradient(180deg,#FAFAFA,#E9EAEB)] tw-inline-flex tw-items-center tw-justify-center tw-mb-4">
                                <div className="tw-w-[32px] tw-h-[32px] tw-border-4 tw-border-solid tw-rounded-full tw-border-[#D1E9FF] tw-border-t-[#0021A5] tw-animate-spin" />
                            </i>
                            <div>
                                <h5 className="tw-text-base tw-font-medium tw-text-[#181D27] dark:tw-text-[#B2B5BE] tw-mt-0 tw-mb-1">
                                    Fetching {bucketDatasource ? "Buckets" : "Schemas"}
                                </h5>
                                <p className="tw-text-sm tw-text-[#535862] dark:tw-text-[#787B86] tw-my-0">
                                    {bucketDatasource
                                        ? `Connecting to ${datasourceType === "azure_blob" ? "Azure Blob Storage" : datasourceType === "s3" ? "AWS S3" : datasourceType}`
                                        : `Connecting to ${datasourceType}`}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {fetchedSchemaNames !== null && !schemaFetchError && (
                                <div className="tw-rounded-lg tw-border tw-border-solid tw-border-[#ABEFC6] tw-bg-[#F6FEF9] tw-py-2 tw-px-3 tw-mb-3">
                                    <div className="d-flex align-items-center tw-gap-[10px]">
                                        <i><CircleCheck size={18} color="#17B26A" /></i>
                                        <div>
                                            <span className="tw-block tw-text-sm tw-text-[#067647] tw-font-medium">
                                                Successfully fetched {fetchedSchemaNames.length} {bucketDatasource ? "buckets" : "schemas"}
                                            </span>
                                            <span className="tw-block tw-text-xs tw-text-[#079455]">
                                                {bucketDatasource
                                                    ? "Select the buckets you want to work with. You can select multiple buckets."
                                                    : "Select the schemas you want to work with. You can select multiple schemas."}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {schemaFetchError && (
                                <div className="alert alert-danger tw-mb-0" role="alert">
                                    {schemaFetchError}
                                </div>
                            )}
                            <div className="tw-flex tw-items-center tw-gap-3">
                                <div className="tw-relative tw-flex-1">
                                    <Search size={16} className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-[#A4A7AE]" />
                                    <input
                                        type="text"
                                        className="form-control tw-pl-9 tw-pr-9"
                                        value={schemaSearchText}
                                        onChange={(e) => setSchemaSearchText(e.target.value)}
                                        placeholder={`Search ${schemaLabel.toLowerCase()}s`}
                                    />
                                    {schemaSearchText && (
                                        <button
                                            type="button"
                                            className="tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-bg-transparent tw-border-0 tw-p-1 tw-text-[#717680] hover:tw-text-[#101828] tw-cursor-pointer"
                                            aria-label="Clear schema search"
                                            title="Clear search"
                                            onClick={() => setSchemaSearchText("")}
                                        >
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline btn-grey tw-whitespace-nowrap"
                                    onClick={handleSelectAllSchemas}
                                    disabled={availableSchemaNames.length === 0}
                                >
                                    {selectedSchemas.length === availableSchemaNames.length && availableSchemaNames.length > 0 ? "Clear All" : "Select All"}
                                </button>
                            </div>

                            <div className="tw-max-h-[360px] tw-overflow-y-auto tw-border tw-border-solid tw-border-[#E9EAEB] dark:tw-border-[#2B2B40] tw-rounded-lg">
                                {filteredSchemaNames.length > 0 ? filteredSchemaNames.map((schema) => {
                                    const isSelected = selectedSchemas.includes(schema);
                                    return (
                                        <label
                                            key={schema}
                                            className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3 tw-border-0 tw-border-b tw-border-solid tw-border-[#E9EAEB] dark:tw-border-[#2B2B40] last:tw-border-b-0 tw-cursor-pointer hover:tw-bg-[#F9FAFB] dark:hover:tw-bg-[#252535]"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => handleSchemaModalSelect(schema)}
                                            />
                                            <FolderTree size={15} className="tw-text-[#175CD3] dark:tw-text-[#53B1FD]" />
                                            <span className="tw-text-sm tw-text-[#101828] dark:tw-text-white">{schema}</span>
                                            {pendingSchemaAdditions.includes(schema) && (
                                                <span className="tw-text-xs tw-font-medium tw-text-[#067647]">will be added</span>
                                            )}
                                            {pendingSchemaRemovals.includes(schema) && (
                                                <span className="tw-text-xs tw-font-medium tw-text-[#F04438]">will be removed</span>
                                            )}
                                        </label>
                                    );
                                }) : (
                                    <div className="tw-px-4 tw-py-5 tw-text-sm tw-text-[#535862] dark:tw-text-[#A1A5B7]">
                                        No {schemaLabel.toLowerCase()}s available
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                <div className="tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-1 tw-text-xs" aria-live="polite">
                    {pendingSchemaAdditions.length > 0 && (
                        <span className="tw-font-medium tw-text-[#067647]">
                            {pendingSchemaAdditions.length} {schemaLabel.toLowerCase()}{pendingSchemaAdditions.length === 1 ? "" : "s"} will be added
                        </span>
                    )}
                    {pendingSchemaRemovals.length > 0 && (
                        <span
                            className="tw-font-medium tw-text-[#F04438]"
                            style={{ marginLeft: pendingSchemaAdditions.length > 0 ? 16 : 0 }}
                        >
                            {pendingSchemaRemovals.length} {schemaLabel.toLowerCase()}{pendingSchemaRemovals.length === 1 ? "" : "s"} will be removed
                        </span>
                    )}
                </div>
                <div className="tw-flex tw-items-center tw-gap-2 tw-ml-auto">
                    <button
                        type="button"
                        className="btn btn-outline btn-grey"
                        onClick={() => setShowSchemaModal(false)}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary tw-inline-flex tw-items-center"
                        onClick={handleConfirmSchemaSelection}
                        disabled={selectedSchemas.length === 0}
                    >
                        <Check size={18} color="currentColor" className="mr-1" />
                        {pendingSchemaAdditions.length > 0 || pendingSchemaRemovals.length > 0
                            ? "Confirm Changes"
                            : "Confirm Selection"} ({selectedSchemas.length})
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
        <Modal
            show={Boolean(schemaRemovalCandidate)}
            onHide={() => setSchemaRemovalCandidate(null)}
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>Remove {schemaLabel.toLowerCase()}?</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                Removing <strong>{Array.isArray(schemaRemovalCandidate) ? schemaRemovalCandidate.join(", ") : schemaRemovalCandidate}</strong> will unassign it from its configured integration.
                Confirm the remaining schema selections after this change.
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-outline btn-grey" onClick={() => setSchemaRemovalCandidate(null)}>
                    Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleConfirmSchemaRemoval}>
                    Remove {schemaLabel.toLowerCase()}
                </button>
            </Modal.Footer>
        </Modal>
        <Modal
            show={Boolean(confirmRemoval)}
            onHide={handleCancelRemoval}
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>Remove {confirmRemoval?.type === "integration" ? "integration" : confirmRemoval?.type === "partition" ? "partition" : "dataset"}?</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>
                    Remove <strong>{confirmRemoval?.label || "this item"}</strong>?
                </p>
                {confirmRemoval?.type === "integration" && (() => {
                    const config = integrations.find((item) => item.id === confirmRemoval.configId);
                    const schemaWillBeRemoved = config &&
                        !declaredSchemaNames.includes(config.schema) &&
                        !integrations.some((item) => item.id !== config.id && item.schema === config.schema);
                    return schemaWillBeRemoved ? (
                        <div className="alert alert-warning" role="status">
                            This is the only integration using {schemaLabel.toLowerCase()} <strong>{config.schema}</strong>; it will also be removed from the selected {schemaLabel.toLowerCase()}s.
                        </div>
                    ) : null;
                })()}
                {confirmRemoval?.isPersisted && (
                    <Form.Group className="tw-mb-3">
                        <Form.Label>
                            Please confirm the name of the {confirmRemoval.type === "integration" ? "integration" : confirmRemoval.type === "partition" ? "partition" : "dataset"} to remove: <strong>{confirmRemoval.label}</strong>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            autoComplete="off"
                            placeholder={`Enter ${confirmRemoval.type} name to confirm`}
                            value={removalConfirmationText}
                            onChange={(event) => setRemovalConfirmationText(event.target.value)}
                        />
                    </Form.Group>
                )}
                <div className="alert alert-danger tw-mb-0" role="alert">
                    {confirmRemoval?.isPersisted
                        ? confirmRemoval.type === "integration"
                            ? "Deleting this integration is permanent and cannot be undone. Associated integration resources will also be deleted."
                            : confirmRemoval.type === "partition"
                                ? "Deleting this partition is permanent and cannot be undone."
                                : "Deleting this dataset is permanent and cannot be undone. Its associated partition configuration will remain unchanged."
                        : "This unsaved item will be removed from the current edit form."}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-outline btn-grey" onClick={handleCancelRemoval}>
                    Cancel
                </button>
                <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleConfirmRemoval}
                    disabled={confirmRemoval?.isPersisted && removalConfirmationText.trim() !== (confirmRemoval?.label || "").trim()}
                >
                    Remove
                </button>
            </Modal.Footer>
        </Modal>
        </>
    );
});

const StaticViewEdit = ({
    show = true,
    onHide,
    onCancel,
    onSave,
    saveErrorMessage = '',
    isSaving = false,
    isSaveDisabled = false,
    title = "View Datasource",
    badgeText = "POSTGRESQL",
    subtitle = "Everest-Demo",
    icon: IconComponent = Eye,
    initialMode = "view",
    disableEditTab = false,
    contentType = "datasource",
    onEditIntegration,
    datasource = {},
    integrations = EMPTY_ARRAY,
    existingDatasources = EMPTY_ARRAY,
    onTestConnection,
    onFetchSchemas,
}) => {
    const [activeTab, setActiveTab] = useState(disableEditTab ? "view" : initialMode);
    const [isOpen, setIsOpen] = useState(true);
    const [isExpanded, setIsExpanded] = useState(true);
    const [editHasValidationErrors, setEditHasValidationErrors] = useState(true);
    const [editDraft, setEditDraft] = useState(null);
    const handleDraftChange = useCallback((draft) => setEditDraft(draft), []);

    useEffect(() => {
        if (show) {
            setIsOpen(true);
            setActiveTab(disableEditTab ? "view" : initialMode);
        }
    }, [show, initialMode, disableEditTab]);

    const handleHide = () => {
        setIsOpen(false);
        if (onHide) onHide();
    };

    const handleCancel = () => {
        setIsOpen(false);
        if (onCancel) {
            onCancel();
        } else if (onHide) {
            onHide();
        }
    };

    const handleSave = () => {
        if (editHasValidationErrors || !editDraft) return;
        if (onSave) {
            onSave(editDraft);
        }
    };

    const handleEditIntegration = useCallback((sourceIntegration) => {
        if (!disableEditTab) {
            setActiveTab("edit");
            setEditHasValidationErrors(true);
        }
        if (onEditIntegration) {
            onEditIntegration(sourceIntegration);
        }
    }, [disableEditTab, onEditIntegration]);

    const displayTitle = title === "View Datasource" && activeTab === "edit" ? "Edit Datasource" : title;
    const CurrentIcon = activeTab === "edit" && IconComponent === Eye ? SquarePen : IconComponent;

    return (
        <Modal
            show={show && isOpen}
            dialogClassName={`modal-vertical shadow ${isExpanded ? "modal-expanded-75 !tw-w-[75vw] !tw-max-w-[75vw]" : ""}`}
            className="fixed-right model__help modal-integration"
            size="xl"
            onHide={handleHide}
            centered
        >
            <Modal.Header className="!tw-px-6 !tw-py-3 align-items-center" closeButton>
                <div className="tw-flex tw-items-center tw-gap-4">
                    <div className="tw-flex tw-items-center tw-justify-center tw-p-[10px] tw-border tw-border-solid tw-border-[#D5D7DA] dark:tw-border-white/20 tw-bg-white dark:tw-bg-white/10 tw-rounded-[10px]">
                        <CurrentIcon size={20} color="currentColor" className="tw-text-[#414651] dark:tw-text-white" />
                    </div>
                    <div>
                        <h5 className="modal-title !tw-m-0 !tw-mb-[2px] tw-font-semibold !tw-text-gray-900 dark:!tw-text-white">
                            {displayTitle}
                        </h5>
                        <div className="tw-flex tw-items-center tw-gap-2">
                            {badgeText && (
                                <div className="tw-px-2 tw-py-[2px] tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-[#175CD3] dark:!tw-text-white tw-border tw-border-solid tw-border-[#B2DDFF] dark:!tw-border-white/30 dark:!tw-bg-white/10 tw-rounded-[6px] tw-uppercase">
                                    {getDatasourceIcon(badgeText)}
                                    <span>{badgeText}</span>
                                </div>
                            )}
                            {subtitle && (
                                <strong className="tw-text-sm tw-text-[#535862] dark:!tw-text-white !tw-font-medium tw-ms-1">
                                    {subtitle}
                                </strong>
                            )}
                        </div>
                    </div>
                </div>

                {/* Expand / Collapse Button */}
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsExpanded(!isExpanded);
                    }}
                    className="tw-absolute tw-right-[56px] tw-top-1/2 -tw-translate-y-1/2 tw-p-1.5 tw-rounded-md tw-bg-transparent tw-border-0 tw-text-[#475467] dark:tw-text-white hover:tw-text-[#101828] dark:hover:tw-text-white hover:tw-bg-black/5 dark:hover:tw-bg-white/15 tw-transition-colors tw-cursor-pointer tw-flex tw-items-center tw-justify-center"
                    title={isExpanded ? "Collapse modal" : "Expand to 75% width"}
                    aria-label={isExpanded ? "Collapse modal to default width" : "Expand modal to 75% of screen width"}
                >
                    {isExpanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                </button>
            </Modal.Header>

            <Modal.Body className="tw-overflow-x-hidden !tw-pt-0">
                <Tab.Container activeKey={activeTab} onSelect={(key) => {
                    if (key === "edit" && disableEditTab) return;
                    setActiveTab(key);
                    setEditHasValidationErrors(key !== "edit");
                }}>
                    <div className="tabbable-line tw-sticky tw-top-0 tw-z-10 tw-bg-white dark:tw-bg-[#1E1E2D] tw-pt-3 tw-pb-2 tw-mb-4">
                        <Nav variant="tabs">
                            <Nav.Item>
                                <Nav.Link eventKey="view">
                                    <span className="qd-menu__link-text">View</span>
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="edit" disabled={disableEditTab}>
                                    <span className="qd-menu__link-text">Edit</span>
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                    </div>

                    <Tab.Content>
                        <Tab.Pane eventKey="view">
                            {activeTab === "view" && <ViewModeContent datasource={datasource} integrations={integrations} contentType={contentType} onEditIntegration={handleEditIntegration} />}
                        </Tab.Pane>
                        <Tab.Pane eventKey="edit">
                            {activeTab === "edit" && (
                                <EditModeContent
                                    datasource={datasource}
                                    integrations={integrations}
                                    existingDatasources={existingDatasources}
                                    contentType={contentType}
                                    onValidationChange={setEditHasValidationErrors}
                                    onDraftChange={handleDraftChange}
                                    onTestConnection={onTestConnection}
                                    onFetchSchemas={onFetchSchemas}
                                />
                            )}
                        </Tab.Pane>
                    </Tab.Content>
                </Tab.Container>
            </Modal.Body>

            <Modal.Footer className='justify-content-between'>
                <div className="tw-max-w-[60%] tw-text-left" aria-live="polite">
                    {saveErrorMessage && (
                        <span role="alert" className="tw-text-xs tw-font-medium tw-text-[#DC2626]">
                            {saveErrorMessage}
                        </span>
                    )}
                </div>
                <div>
                    <button className="btn btn-outline btn-grey tw-mr-3" onClick={handleCancel}>Cancel</button>
                    {activeTab === "edit" && (
                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={isSaving || isSaveDisabled || editHasValidationErrors}
                            onClick={handleSave}
                        >
                            {isSaving ? (
                                <div className="tw-flex tw-items-center tw-justify-center">
                                    <div className="tw-w-[14px] tw-h-[14px] tw-border tw-border-solid tw-rounded-full tw-border-white tw-border-t-transparent tw-animate-spin"></div>
                                    <span className='tw-ml-2'>Saving...</span>
                                </div>
                            ) : "Save Changes"}
                        </button>
                    )}
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default StaticViewEdit;
